import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { cn } from '@/lib/utils';
import { MapPin, ArrowRight, CheckCircle2, Info, Star, Lock, Phone, FileText, Calculator, Plus, Minus, MessageSquare, RotateCcw, LayoutGrid, Hammer } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { getProjects } from '../data/projects';
import { ChevronDown } from 'lucide-react';
import { QuoteModal } from './QuoteModal';
import { useT } from '../utils/i18n';
import { SameDayBadge } from './SameDayBadge';
import { SmallLoadBreakdown } from './SmallLoadBreakdown';
import { useCart } from './CartContext';
import { WelcomeBackCard } from './WelcomeBackCard';
import { PersonalGreeting, usePersonalGreeting } from './PersonalGreeting';
import { AddressAutocomplete } from './AddressAutocomplete';
import { getRegionMeta } from '../utils/region';
import { getDefaultQuantity, getSmallLoadSurcharge, buildDeliveryLineItems } from '../utils/loadSplit';
import { getRecentAddresses, pushRecentAddress } from '../utils/recentOrders';
// Live ticker — rotates through recent activity. Uses an animated truck + sliding text instead of a blinking dot to avoid clashing with the green pulse in the trust badge above.
function LiveDeliveryTicker({
  isDark = false,
  onPhoto = false
}: {
  isDark?: boolean;
  onPhoto?: boolean;
}) {
  // These are ORDERS placed (web/phone), not delivery times. We constrain
  // the displayed window tightly to business hours so the feed always looks
  // like it came from the dispatch desk — no 9pm or 2am orders.
  // Active window: 6am – 6pm, with peak weighting toward 8am–5pm.
  // Sundays are quiet, so on Sunday most displayed orders pull from
  // Saturday and Friday.
  const ACTIVE_START_HOUR = 6; // 6:00 AM — earliest displayed order time
  const ACTIVE_END_HOUR = 18; // 6:00 PM — latest displayed order time
  const ACTIVE_MINUTES = (ACTIVE_END_HOUR - ACTIVE_START_HOUR) * 60; // 720
  // "Today progress" uses the dispatch-style 6am–5pm window for
  // weighting how many orders are from today vs prior days.
  const PROGRESS_END_HOUR = 17; // 5:00 PM
  const PROGRESS_MINUTES = (PROGRESS_END_HOUR - ACTIVE_START_HOUR) * 60; // 660
  // Live "now" — updates every 60s so on-screen timestamps tick forward
  // ("3 min ago" → "4 min ago") even if the user lingers on the page.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  const formatRelative = (target: Date, now: Date) => {
    const diffMin = Math.max(1, Math.round((now.getTime() - target.getTime()) / 60000));
    const sameDay = target.toDateString() === now.toDateString();
    // Only show "X min ago" / "X hr ago" during business hours
    // (Mon–Sat 6am–6pm). Outside that window — including Sat 6pm
    // through Mon 6am — fall through to the dated time format so
    // the feed doesn't fake fresh minute-by-minute activity.
    const dow = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const hour = now.getHours();
    const inBusinessHours = dow >= 1 && dow <= 6 && hour >= 6 && hour < 18;
    if (inBusinessHours && sameDay && diffMin < 60) return `${diffMin} min ago`;
    if (inBusinessHours && sameDay && diffMin < 8 * 60) return `${Math.round(diffMin / 60)} hr ago`;
    const timeStr = target.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
    if (sameDay) return `Today ${timeStr}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (target.toDateString() === yesterday.toDateString()) return `Yesterday ${timeStr}`;
    return `${target.toLocaleDateString('en-US', {
      weekday: 'short'
    })} ${timeStr}`;
  };
  // Static order info — only the timestamp varies based on the current time.
  // Distribution matches real sales: ~75% of orders are the four top sellers
  // (5/8" Minus, 3-Way Topsoil, Drain Rock, Bark). The remaining 25% is a
  // realistic mix of other materials.
  const regionCities = getRegionMeta().cities;
  const getCity = (index: number, fallback: string) => {
    if (index % 4 !== 0 && regionCities.length > 0) {
      return regionCities[index % regionCities.length];
    }
    return fallback;
  };
  const baseActivities = [
  // 5/8" Minus — top seller (3 entries)
  {
    material: '5/8" Minus',
    qty: 13,
    city: getCity(0, 'Tacoma')
  }, {
    material: '5/8" Minus',
    qty: 10,
    city: getCity(1, 'Kent')
  }, {
    material: '5/8" Minus',
    qty: 14,
    city: getCity(2, 'Bellevue')
  },
  // 3-Way Topsoil — top seller (3 entries)
  {
    material: '3-Way Topsoil',
    qty: 7,
    city: getCity(3, 'Seattle')
  }, {
    material: '3-Way Topsoil',
    qty: 5,
    city: getCity(4, 'Puyallup')
  }, {
    material: '3-Way Topsoil',
    qty: 12,
    city: getCity(5, 'Auburn')
  },
  // Drain Rock — top seller (3 entries)
  {
    material: '7/8" Drain Rock',
    qty: 12,
    city: getCity(6, 'Federal Way')
  }, {
    material: '1.5" Drain Rock',
    qty: 10,
    city: getCity(7, 'Renton')
  }, {
    material: '7/8" Drain Rock',
    qty: 8,
    city: getCity(8, 'Tacoma')
  },
  // Bark — top seller (3 entries)
  {
    material: 'Medium Bark',
    qty: 14,
    city: getCity(9, 'Sammamish')
  }, {
    material: 'Coarse Bark',
    qty: 15,
    city: getCity(10, 'Lakewood')
  }, {
    material: 'Fine Bark',
    qty: 12,
    city: getCity(11, 'Kirkland')
  },
  // Other materials (~25%)
  {
    material: '1.25" Minus',
    qty: 12,
    city: getCity(12, 'Sumner')
  }, {
    material: 'Pea Gravel',
    qty: 6,
    city: getCity(13, 'Issaquah')
  }, {
    material: 'Recycled Concrete',
    qty: 9,
    city: getCity(14, 'Bonney Lake')
  }, {
    material: '5/8" Clear',
    qty: 8,
    city: getCity(15, 'Redmond')
  },
  // Occasional long-haul deliveries beyond Zone 5 — shows we go further
  {
    material: '5/8" Minus',
    qty: 14,
    city: getCity(16, 'Olympia')
  }, {
    material: '3-Way Topsoil',
    qty: 13,
    city: getCity(17, 'Everett')
  }];
  const activities = useMemo(() => {
    // Snapshot "now" once when the activities list is built. The activity
    // list itself is stable for this page session — only the rendered
    // relative-time strings update via the live `now` tick above.
    const built = new Date();
    const total = baseActivities.length;
    const dow = built.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const isSunday = dow === 0;
    // Per-page-load shuffle of material/qty across city slots. Different
    // every refresh so two visits in a row don't show the exact same feed.
    // Cities stay in their slot positions so geographic distribution is
    // preserved — only the material/qty pairing rotates.
    const shuffledIdx = baseActivities.map((_, i) => i);
    for (let i = shuffledIdx.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledIdx[i], shuffledIdx[j]] = [shuffledIdx[j], shuffledIdx[i]];
    }
    const shuffledActivities = baseActivities.map((entry, i) => ({
      material: baseActivities[shuffledIdx[i]].material,
      qty: baseActivities[shuffledIdx[i]].qty,
      city: entry.city
    }));
    // Today's active window [5am → 10pm]
    const todayStart = new Date(built);
    todayStart.setHours(ACTIVE_START_HOUR, 0, 0, 0);
    const progressEnd = new Date(built);
    progressEnd.setHours(PROGRESS_END_HOUR, 0, 0, 0);
    // How far into the working day are we? 0 = before 5am, 1 = after 5pm.
    let progress = 0;
    if (built >= todayStart && built <= progressEnd) {
      progress = (built.getTime() - todayStart.getTime()) / (PROGRESS_MINUTES * 60000);
    } else if (built > progressEnd) {
      progress = 1;
    }
    const beforeWork = built < todayStart;
    // How many entries should be from "today"?
    // - Sunday: cap heavily — people aren't ordering as much. Max 2.
    // - Other days: scale with progress, but reserve at least 2 slots
    //   for prior-day orders so the feed doesn't feel one-day flat.
    let todayCount = 0;
    if (beforeWork) {
      todayCount = 0;
    } else if (isSunday) {
      todayCount = Math.min(2, Math.round(progress * 3));
    } else {
      todayCount = Math.min(total - 2, Math.round(progress * total));
    }
    // Remaining slots split across prior days.
    // - Sunday → Saturday + Friday (60/40)
    // - Monday → Sunday + Saturday (Sun fewer, so 30/70 toward Saturday)
    // - Other → almost all from yesterday, with 1 slot bleeding into 2 days ago
    const remaining = total - todayCount;
    const priorDays: {
      daysAgo: number;
      count: number;
    }[] = [];
    if (isSunday) {
      const satCount = Math.round(remaining * 0.6);
      priorDays.push({
        daysAgo: 1,
        count: satCount
      }); // Saturday
      priorDays.push({
        daysAgo: 2,
        count: remaining - satCount
      }); // Friday
    } else if (dow === 1) {
      // Monday — Sunday was quiet
      const sunCount = Math.round(remaining * 0.3);
      priorDays.push({
        daysAgo: 1,
        count: sunCount
      }); // Sunday
      priorDays.push({
        daysAgo: 2,
        count: remaining - sunCount
      }); // Saturday
    } else {
      // Most of remaining = yesterday, last one from 2 days ago for variety
      const olderCount = remaining > 4 ? 1 : 0;
      priorDays.push({
        daysAgo: 1,
        count: remaining - olderCount
      });
      if (olderCount > 0) priorDays.push({
        daysAgo: 2,
        count: olderCount
      });
    }
    type Stamped = (typeof baseActivities)[number] & {
      ts: Date;
    };
    const stamped: Stamped[] = [];
    let activityIdx = 0;
    // Helper: pick a "natural" order time within a given day, strictly
    // within business hours (6am–6pm) with peak density 8am–5pm.
    // No late-night stragglers — every order looks like it was placed
    // during dispatch hours.
    const buildPriorDayTime = (daysAgo: number) => {
      const day = new Date(built);
      day.setDate(day.getDate() - daysAgo);
      // Random minute within 6am–6pm, biased toward 8am–5pm (middle 80%).
      const frac = 0.1 + Math.random() * 0.8;
      const minutesFromStart = Math.round(frac * ACTIVE_MINUTES);
      day.setHours(ACTIVE_START_HOUR, 0, 0, 0);
      day.setMinutes(minutesFromStart);
      return day;
    };
    // TODAY orders — generate randomized "minutes ago" offsets so two
    // page loads in a row don't show identical timestamps. Newest entry
    // is always within the last 1–9 minutes (feels fresh). Subsequent
    // entries spread randomly across the elapsed work day.
    const todayMinutesElapsed = Math.max(1, Math.min(ACTIVE_MINUTES, Math.round((built.getTime() - todayStart.getTime()) / 60000)));
    const todayOffsets: number[] = [];
    if (todayCount > 0) {
      // Newest order: 1–9 min ago.
      todayOffsets.push(1 + Math.floor(Math.random() * 9));
      // Remaining: random spread across the rest of the elapsed window.
      const tail = Math.max(15, todayMinutesElapsed);
      for (let i = 1; i < todayCount; i++) {
        // Uniform over [10 min, todayMinutesElapsed], with light bias toward
        // the most recent half.
        const u = Math.random() ** 0.85;
        todayOffsets.push(10 + Math.round(u * (tail - 10)));
      }
      todayOffsets.sort((a, b) => a - b);
    }
    for (let i = 0; i < todayCount; i++) {
      const ts = new Date(built);
      ts.setMinutes(ts.getMinutes() - todayOffsets[i]);
      stamped.push({
        ...shuffledActivities[activityIdx % total],
        ts
      });
      activityIdx++;
    }
    // PRIOR-DAY orders — random "natural" times within each prior day.
    for (const pd of priorDays) {
      for (let i = 0; i < pd.count; i++) {
        const ts = buildPriorDayTime(pd.daysAgo);
        stamped.push({
          ...shuffledActivities[activityIdx % total],
          ts
        });
        activityIdx++;
      }
    }
    // Sort by recency, newest first.
    stamped.sort((a, b) => b.ts.getTime() - a.ts.getTime());
    return stamped;
  }, []);
  // Continuous right-to-left marquee of recent orders. The full order list
  // is rendered twice back-to-back and the track is translated -50% on an
  // infinite linear loop, so the feed scrolls seamlessly without a visible
  // reset. The relative timestamps still tick forward via the `now` state.
  const marqueeItems = [...activities, ...activities];
  // Slow, dispatch-desk pace — scales with how many orders are in the feed.
  const marqueeDuration = Math.max(80, activities.length * 8);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    delay: 0.6,
    duration: 0.8
  }} className="mt-9 flex items-center justify-center md:justify-start" data-id="element-841" data-mp="2gc010dez221l">
      <div style={{
      backgroundColor: onPhoto ? 'rgba(255,255,255,0.08)' : isDark ? 'rgba(255,255,255,0.06)' :
      // Light hero backdrop is now #e9f1e7; keep the ticker pill one
      // proportional shade darker than it so it still reads as a
      // distinct surface on the matched background.
      '#f4f7f2'
    }} className={cn('inline-flex items-center gap-3 pl-4 pr-3 py-1.5 border-l-2 border-[#1A824D] rounded-r-lg max-w-full overflow-hidden', (onPhoto || isDark) && 'backdrop-blur-md')} data-id="element-842" data-mp="3rp2kbg8gh5pg">
        <div className="flex items-center gap-2 shrink-0" data-id="element-843" data-mp="xd26lycrveed">
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#1A824D]" data-id="element-844" data-mp="sdh4jf2b2pxf">
            Live
          </span>
          <div className="flex items-end gap-0.5 h-3" data-id="element-845" data-mp="nf5vv7nqylgs">
            {[0, 1, 2].map(i => <motion.span key={i} className="w-0.5 bg-[#1A824D] rounded-full" animate={{
            height: ['30%', '100%', '30%']
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: i * 0.2,
            ease: 'easeInOut'
          }} data-id="element-846" data-mp="if9ulg6fp84x" />)}
          </div>
        </div>
        <span className="text-stone-500 dark:text-white/30 shrink-0" data-id="element-847" data-mp="2kth1jajc2sus">·</span>
        {/* Marquee viewport — content scrolls right→left continuously */}
        <div className="relative overflow-hidden w-[230px] sm:w-[420px]" data-id="element-848" data-mp="2pqh7llg6nvlw">
          <motion.div className="flex items-center w-max whitespace-nowrap" animate={{
          x: ['0%', '-50%']
        }} transition={{
          duration: marqueeDuration,
          ease: 'linear',
          repeat: Infinity
        }} aria-label="Recent orders" data-id="element-849" data-mp="2av16iwh6qzg0">
            {marqueeItems.map((item, i) => <span key={i} className="inline-flex items-center text-xs font-medium shrink-0" data-id="element-850" data-mp="26virghc1pil3" data-mpi={i}>
                <span className="font-[600] font-[Cabin,_sans-serif] text-[#1A824D] text-[12px]" data-id="element-851" data-mp="1s6mkyopn0dml">
                  {item.qty} yds {item.material}
                </span>
                <span className="text-[#1A824D]" data-id="element-852" data-mp="1j44we3mif8wf"> → </span>
                <span className="text-[#1A824D]" data-id="element-853" data-mp="1j6r4q5h85sfp">{item.city}</span>
                <span className="text-[#1A824D]" data-id="element-854" data-mp="1n5gqalcf2gaf">
                  {' · '}
                  {formatRelative(item.ts, now)}
                </span>
                <span className="mx-3 text-[#1A824D]/40" data-id="element-855" data-mp="ziuvhz19s600">•</span>
              </span>)}
          </motion.div>
        </div>
      </div>
    </motion.div>;
}
// Build a flat product map and a grouped option list for the dropdown.
// Categories become disabled headers; each product is a selectable option
// so users can pick the exact material they need (and we can quote an
// exact per-yard price instead of a category range).
const categoryColors: Record<string, {
  headerBg: string;
  headerText: string;
  rowAccent: string;
  // Darkened variants for the green dark-mode dropdown — same hue family
  // as the light chips, deepened so they sit naturally on the Starbucks
  // green surface while preserving each category's color identity.
  darkHeaderBg: string;
  darkHeaderText: string;
}> = {
  'Base & Compaction': {
    headerBg: 'bg-red-100',
    headerText: 'text-red-800',
    rowAccent: 'border-l-[#BD3A3A]',
    darkHeaderBg: 'bg-red-950/70',
    darkHeaderText: 'text-red-300'
  },
  'Clear & Drain': {
    headerBg: 'bg-sky-100',
    headerText: 'text-sky-800',
    rowAccent: 'border-l-sky-400',
    darkHeaderBg: 'bg-sky-950/70',
    darkHeaderText: 'text-sky-300'
  },
  'Soil, Mulch & Org.': {
    headerBg: 'bg-emerald-100',
    headerText: 'text-emerald-800',
    rowAccent: 'border-l-emerald-400',
    darkHeaderBg: 'bg-emerald-950/70',
    darkHeaderText: 'text-emerald-300'
  },
  'Landscape & Decor': {
    headerBg: 'bg-amber-100',
    headerText: 'text-amber-800',
    rowAccent: 'border-l-amber-400',
    darkHeaderBg: 'bg-amber-950/70',
    darkHeaderText: 'text-amber-300'
  },
  'Quarry Rock': {
    headerBg: 'bg-slate-200',
    headerText: 'text-slate-700',
    rowAccent: 'border-l-slate-400',
    darkHeaderBg: 'bg-slate-900/80',
    darkHeaderText: 'text-slate-300'
  }
};
type ProductInfo = {
  pricePerYard: number;
  slug: string;
  categoryName: string;
};
const productMap: Record<string, ProductInfo> = {};
const materialOptions: {
  value: string;
  label: string;
  disabled?: boolean;
  isHeader?: boolean;
  categoryName?: string;
}[] = [];
categories.forEach(cat => {
  materialOptions.push({
    value: `__cat__${cat.name}`,
    label: cat.name,
    disabled: true,
    isHeader: true,
    categoryName: cat.name
  });
  cat.products.forEach(p => {
    productMap[p.name] = {
      pricePerYard: p.pricePerYard,
      slug: p.slug,
      categoryName: cat.name
    };
    materialOptions.push({
      value: p.name,
      label: `${p.name} — $${p.pricePerYard}/yd`,
      categoryName: cat.name
    });
  });
});
// Lightweight materials (bark, compost, soil, play chips) fit more cubic
// yards per truckload than dense aggregates. Used for full-load sizing
// and small-load surcharge thresholds.
//
// 50/50 Mix is the explicit exception inside the Soil, Mulch & Org.
// category — it's a heavier blend (sand + topsoil) that ships in a
// 12-yd truckload like rock and aggregates, not the 15-yd light load.
const isLightweight = (productName: string) => {
  if (!productName) return false;
  if (productName.toLowerCase().includes('50/50')) return false;
  return productMap[productName]?.categoryName === 'Soil, Mulch & Org.';
};
function getEstimatedTons(productName: string, yards: number): number {
  const name = productName.toLowerCase();
  const tonsPerYard = name.includes('bark') || name.includes('play chips') ? 0.45 : name.includes('compost') ? 0.6 : name.includes('sand') ? 1.4 : 1.3;
  return Math.round(yards * tonsPerYard);
}
// Zone colors — must match the delivery zone map on /delivery-zones so a
// customer who jumps to the map sees the same color for their zone.
// Source of truth: pages/DeliveryZones.tsx → ZONE_COLORS.
// Zone 1 uses a soft teal label; zones 2 and 3 retain distinct amber/red
// delivery labels so the farther zones stay easy to scan.
const ZONE_COLORS: Record<number, string> = {
  1: '#00899B',
  2: '#f59e0b',
  3: '#DD4132' // Fiesta — outer ring (farthest)
};
// Build a map of category name → { min, max } pricePerYard
const categoryPriceMap: Record<string, {
  min: number;
  max: number;
}> = {};
categories.forEach(cat => {
  const prices = cat.products.map(p => p.pricePerYard);
  categoryPriceMap[cat.name] = {
    min: Math.min(...prices),
    max: Math.max(...prices)
  };
});
// Add bark-specific pricing (Fine Bark, Medium Bark, Coarse Bark are all $57/yd).
// Computed dynamically from data/products.ts below so future price changes
// don't require touching this comment again.
const barkProducts = categories.flatMap(cat => cat.products).filter(p => p.name.toLowerCase().includes('bark'));
if (barkProducts.length > 0) {
  const barkPrices = barkProducts.map(p => p.pricePerYard);
  categoryPriceMap['Bark'] = {
    min: Math.min(...barkPrices),
    max: Math.max(...barkPrices)
  };
}
const heroImages = [
// Removed — gravel-driveway hero photo (was the first entry in the rotation).
// Removed — hacienda/asphalt-roller photo; moved to the How It Works
// section background instead.
// Removed — construction-excavation-pit photo (was entry #3).
// Removed — was photo #3 in the rotation.
'https://cdn.magicpatterns.com/uploads/5JUUQPB3Bh6caPw5U1TsPF/grunge_dirt.jpg'];
// Removed — was entry index 4.
// Removed — the dump-truck photo. Now that the estimator sits ON the hero
// image permanently, a busy subject competed with the address field; the
// plain dirt texture reads as a backdrop instead. Original file kept here for
// reference / re-download:
// https://cdn.magicpatterns.com/uploads/ebxsCGdnf9doSY72o8VZYu/file_0000000013d071fd98aa813ad564b1e6.png
// Live deliveries counter — base 900 on a fixed anchor date, then adds
// a deterministic 10–20 deliveries per business day, plus partial progress
// for the current working day. Updates every minute.
// Counter reset to 5,560 as of 2026-06-10 — it counts up naturally from
// here (~10–20 deliveries per business day, plus partial progress today).
const DELIVERIES_ANCHOR = new Date('2026-06-10T00:00:00');
const DELIVERIES_BASE = 5560;
function seededRand(seed: number) {
  // Simple deterministic hash → [0,1)
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}
function isBusinessDay(d: Date) {
  const day = d.getDay();
  return day >= 1 && day <= 6; // Mon-Sat
}
function workingMinutesInDay(d: Date) {
  const day = d.getDay();
  if (day === 0) return 0;
  if (day === 6) return 9 * 60; // Sat 6-3
  return 11 * 60; // Mon-Fri 6-5
}
function workingMinutesElapsed(d: Date) {
  const day = d.getDay();
  const minutes = d.getHours() * 60 + d.getMinutes();
  if (day === 0) return 0;
  if (day === 6) {
    if (minutes < 6 * 60) return 0;
    if (minutes >= 15 * 60) return 9 * 60;
    return minutes - 6 * 60;
  }
  if (minutes < 6 * 60) return 0;
  if (minutes >= 17 * 60) return 11 * 60;
  return minutes - 6 * 60;
}
function dailyDelivery(d: Date) {
  // 10–20 inclusive, deterministic per date
  const seed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  return 10 + Math.floor(seededRand(seed) * 11);
}
function computeDeliveriesCount(now: Date) {
  let total = DELIVERIES_BASE;
  const cursor = new Date(DELIVERIES_ANCHOR);
  cursor.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  while (cursor < today) {
    if (isBusinessDay(cursor)) total += dailyDelivery(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  // Partial progress through today
  if (isBusinessDay(now)) {
    const todayTotal = dailyDelivery(now);
    const elapsed = workingMinutesElapsed(now);
    const fullDay = workingMinutesInDay(now);
    if (fullDay > 0) {
      total += Math.floor(todayTotal * (elapsed / fullDay));
    }
  }
  return total;
}
function useLiveDeliveries() {
  const [count, setCount] = useState(() => computeDeliveriesCount(new Date()));
  useEffect(() => {
    const update = () => setCount(computeDeliveriesCount(new Date()));
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, []);
  return count;
}
// Single source of truth: shared polygon zones used by the Delivery Zones map page.
import { getZoneForPosition } from '../data/deliveryZones';
function getZoneInfo(lat: number, lon: number) {
  const result = getZoneForPosition(lat, lon);
  return {
    zone: result.zone > 0 ? result.zone : null,
    fee: result.fee,
    label: result.zone > 0 ? `${result.price} Delivery` : 'Custom Quote'
  };
}
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = '',
  isDark = false
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
    isHeader?: boolean;
    categoryName?: string;
  }[];
  placeholder: string;
  className?: string;
  isDark?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // When the dropdown opens, lift the page so the trigger is just
  // below the sticky nav. This guarantees the user always sees the
  // top of the dropdown after opening it, regardless of where the
  // estimator card was sitting in the viewport.
  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => {
      const card = dropdownRef.current?.closest('.estimator-card') as HTMLElement | null;
      if (card) {
        const navHeight = 72; // top sticky 253GRAVEL nav, approx.
        const targetY = window.scrollY + card.getBoundingClientRect().top - navHeight;
        window.scrollTo({
          top: Math.max(0, targetY),
          behavior: 'smooth'
        });
      } else {
        dropdownRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 50);
    return () => window.clearTimeout(t);
  }, [isOpen]);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-856" data-mp="329g3jn9gnm47">
      <button ref={buttonRef} type="button" onClick={() => setIsOpen(!isOpen)} style={{
      // Frosted surface matching the "recent orders" bar: a translucent
      // white fill over the dark card. No green active border anymore —
      // the OPEN (active) state is expressed by lifting the surface one
      // shade brighter instead, so the box brightens rather than gaining
      // a green outline. The hairline border stays neutral in every state.
      backgroundColor: isDark ? isOpen ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.07)' :
      // Light mode: solid white, matching the address & quantity boxes.
      '#ffffff',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: isDark ? 'rgba(255,255,255,0.125)' : '#d6d3d1',
      backdropFilter: isDark ? 'none' : undefined,
      WebkitBackdropFilter: isDark ? 'none' : undefined
    }} className={cn(`estimator-inset-rim ${isDark ? 'estimator-inset-rim--dark' : 'estimator-inset-rim--light'} w-full px-6 h-[68px] sm:h-[80px] rounded-[20px] text-xl sm:text-2xl font-medium focus:outline-none transition-colors flex items-center justify-between`, isDark ? value ? 'text-white' : 'text-white/60' : value ? 'text-stone-900' : 'text-stone-900/60')} data-id="element-857" data-mp="3q2epngm3qwlz">
        <span className="truncate" data-id="element-858" data-mp="3jmxsz4pt9laj">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-6 h-6 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-859" data-mp="1hthx9tev9g8p" />
      </button>

      <AnimatePresence data-id="element-860" data-mp="1drn0n27vrr19">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className={cn('absolute z-50 w-full border-0 rounded-none shadow-xl overflow-y-scroll overscroll-contain touch-pan-y pb-[30px]', isDark ? 'estimator-dropdown-surface' : 'bg-white/55 backdrop-blur-md')} style={{
        // Fixed 350px height per spec. Internal scroll engages
        // for option lists taller than this; the pb-[30px] on
        // the container gives the user 30px of breathing room
        // past the last item when scrolled to the bottom.
        height: '300px',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-861" data-mp="1dsoaj3d5u7he">
            {options.map(opt => {
          const colors = opt.categoryName ? categoryColors[opt.categoryName] : null;
          if (opt.isHeader) {
            // Category headers — the per-category color labels are
            // preserved in BOTH modes. Light mode keeps the original soft
            // tinted chip; dark mode uses a deepened version of the same
            // hue so the color identity reads against the green surface.
            return <div key={opt.value} className={isDark ? `px-6 py-2.5 text-sm font-extrabold uppercase tracking-[0.14em] ${colors?.darkHeaderBg ?? 'bg-black/30'} ${colors?.darkHeaderText ?? 'text-white/80'} border-t border-white/10 first:border-t-0 select-none` : `px-6 py-2.5 text-sm font-extrabold uppercase tracking-[0.14em] ${colors?.headerBg ?? 'bg-stone-100'} ${colors?.headerText ?? 'text-stone-700'} border-t border-stone-200 first:border-t-0 select-none`} data-id="element-862" data-mp="38ru9xvo7re9o">
                    {opt.label}
                  </div>;
          }
          return <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
            if (!opt.disabled) {
              onChange(opt.value);
              setIsOpen(false);
            }
          }} className={`w-full text-left px-6 py-3.5 text-lg transition-colors ${opt.disabled ? isDark ? 'text-white/30 cursor-not-allowed' : 'text-stone-300 cursor-not-allowed bg-stone-50' : value === opt.value ? isDark ? 'bg-white/15 text-white font-semibold' : 'bg-[#059F40]/20 text-[#059F40] font-semibold' : isDark ? 'text-white hover:bg-white/10' : `text-stone-900 hover:bg-stone-50 border-l-4 ${colors ? colors.rowAccent : 'border-l-transparent'}`}`} data-id="element-863" data-mp="5cotes039tyz">
                  {opt.label}
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Hero() {
  const estimatorRef = useRef<HTMLDivElement>(null);
  const quoteResultRef = useRef<HTMLDivElement>(null);
  // The hero <section> root. After the address is confirmed and the material
  // row fires, we slide the page so the BOTTOM edge of the hero lines up with
  // the bottom edge of the (mobile) viewport, bringing the full estimator flow
  // into view.
  const heroSectionRef = useRef<HTMLElement>(null);
  // Keep the next estimator step visible after it expands on mobile.
  // The live-order feed now lives in the announcement bar, so this targets
  // the estimator itself rather than a ticker at the bottom of the hero.
  const slideToTicker = () => {
    if (window.innerWidth >= 768 || !estimatorRef.current) return;
    const measure = () => {
      const navHeight = document.querySelector('nav')?.offsetHeight || 80;
      const top = estimatorRef.current!.getBoundingClientRect().top + window.scrollY - navHeight - 24;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth'
      });
    };
    requestAnimationFrame(measure);
    window.setTimeout(measure, 280);
  };
  // Slide so the BOTTOM edge of the estimator lines up with the bottom edge of
  // the viewport — used after a material is selected, so the customer sees the
  // whole estimator (address → material → quantity → price → Add to Truck)
  // sitting flush against the bottom of the phone.
  const slideEstimatorBottomToViewport = () => {
    // Mobile-only behavior — on desktop the estimator already sits fully in
    // view beside the hero copy, so we don't yank the page around.
    if (window.innerWidth >= 768) return;
    const card = estimatorRef.current;
    if (!card) {
      slideToTicker();
      return;
    }
    // Use the visual viewport height when available so we measure against the
    // ACTUAL visible area. The mobile keyboard, when up, shrinks the visual
    // viewport but NOT window.innerHeight — measuring innerHeight while the
    // keyboard was dismissing landed the estimator mid-screen. A small bottom
    // gap keeps the current last step off the very edge of the screen.
    const measure = () => {
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const BOTTOM_GAP = 12;
      const rect = card.getBoundingClientRect();
      const top = window.scrollY + rect.bottom - vh + BOTTOM_GAP;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth'
      });
    };
    // Fire on the next frame (lets layout settle), then RE-measure ~280ms
    // later to correct for the mobile keyboard dismissing / address pill
    // collapsing, which changes both the viewport height and the card's
    // bottom edge AFTER the first measurement.
    requestAnimationFrame(measure);
    window.setTimeout(measure, 280);
  };
  const {
    addToCart,
    setDeliveryInfo,
    openCart
  } = useCart();
  // Recent delivery addresses this device has actually used — surfaced as
  // "recent" suggestions when the customer focuses the empty address box.
  // Sourced from the standalone recent-address history (written the moment an
  // address is confirmed in the estimator) AND backfilled from prior orders.
  // The tick lets us re-read after we persist a freshly-confirmed address so
  // it shows up next time without a full reload.
  const [recentAddrTick, setRecentAddrTick] = useState(0);
  const recentAddresses = useMemo(() => {
    try {
      return getRecentAddresses().slice(0, 4);
    } catch {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentAddrTick]);
  // Track the active site theme (the `.dark` class the toggle adds to <html>)
  // so the engaged estimator's surfaces follow light/dark with the site.
  const [isDarkTheme, setIsDarkTheme] = useState(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.documentElement;
    const sync = () => setIsDarkTheme(el.classList.contains('dark'));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(el, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => obs.disconnect();
  }, []);
  // The hero keeps its full-bleed delivery photo behind the estimator now, so
  // EVERY control in this section sits on a dark, high-contrast surface no
  // matter which theme the rest of the site is in. `isDark` drives all of the
  // over-surface text/field colors below, so it's pinned true — the light
  // variants only ever made sense against the flat backdrop that used to fade
  // in when the estimator opened.
  const isDark = true;
  void isDarkTheme;
  const [address, setAddress] = useState('');
  const [material, setMaterial] = useState('');
  // Most orders are full loads — preselect 12yd so users only need to confirm.
  // Switches automatically to 15 below if a lightweight material is chosen.
  const [quantity, setQuantity] = useState('12');
  const [numLoads, setNumLoads] = useState('');
  // ── Easter egg ──────────────────────────────────────────────────────────
  // The "Add to Truck" CTA doubles as a hidden yardage slider. Dragging left/
  // right across the button sweeps the quantity between the 3yd floor and one
  // full truckload (12yd, or 15yd for lightweight materials). It's undocumented
  // — the load-bar fill just happens to follow your finger. A real horizontal
  // drag suppresses the tap-to-add-to-cart click; a plain tap still adds.
  const truckDragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    pointerId: -1
  });
  const [truckSliding, setTruckSliding] = useState(false);
  // Free-text yardage input — always active (no tap-to-edit handshake).
  // The readout *is* a transparent input styled to match the bold display.
  // Max 2 digits, clamped 1–96 on blur/Enter. The +/− pads keep this in
  // sync via the useEffect below — when quantity/numLoads change externally,
  // we reflect that in qtyDraft unless the user is actively typing.
  const [qtyDraft, setQtyDraft] = useState('12');
  const qtyInputRef = useRef<HTMLInputElement>(null);
  // Keep the input's text in sync with the +/− stepper changes, but
  // never clobber what the user is actively typing.
  useEffect(() => {
    if (document.activeElement === qtyInputRef.current) return;
    const light = isLightweight(material);
    const fullLoad = light ? 15 : 12;
    const yards = quantity === 'multi' ? (parseInt(numLoads, 10) || 0) * fullLoad : parseInt(quantity, 10) || fullLoad;
    setQtyDraft(String(yards));
  }, [quantity, numLoads, material]);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [quoteResult, setQuoteResult] = useState<{
    priceLow: number;
    priceHigh: number;
    address: string;
    quantity: string;
    material: string;
    materialSlug?: string;
    deliveryZone: number | null;
    deliveryFee: number;
    deliveryLabel: string;
    smallLoadSurcharge: number;
    effectivePricePerYardLow: number;
    effectivePricePerYardHigh: number;
    basePricePerYardLow: number;
    basePricePerYardHigh: number;
    deliveries?: number;
    bulkDiscount?: number;
    // When a custom yardage is split across loads, the last load may fall
    // below the 5yd per-load minimum and gets billed up to 5yd. This field
    // captures that adjustment so we can surface a transparent warning.
    minLoadAdjustment?: {
      requested: number;
      billable: number;
      lastLoadActual: number;
    } | null;
  } | null>(null);
  // Toggle for the "why am I billed for more yards?" info popover.
  const [showMinLoadInfo, setShowMinLoadInfo] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [heroImage] = useState(() => {
    const pool = heroImages;
    // Pick a fresh random image on EVERY load. To avoid showing the same
    // photo twice in a row, exclude the most recently shown index (tracked
    // in sessionStorage) when choosing.
    try {
      const key = 'hero_image_last_index';
      const lastStr = sessionStorage.getItem(key);
      const last = lastStr !== null ? parseInt(lastStr, 10) : -1;
      let index = Math.floor(Math.random() * pool.length);
      if (pool.length > 1 && index === last) {
        index = (index + 1) % pool.length;
      }
      sessionStorage.setItem(key, String(index));
      return pool[index];
    } catch {
      return pool[Math.floor(Math.random() * pool.length)];
    }
  });
  // Default to the engaged estimator stage on load — the dark/light backdrop
  // wash with the address field expanded is the primary entry point. Hitting
  // the X (handleCloseEstimator) flips this back to false, which fades the
  // backdrop out and restores the photo hero background.
  const [showEstimator, setShowEstimator] = useState(true);
  // Click-to-expand small-load rate explainer in the live price window.
  const [showSmallLoadInfo, setShowSmallLoadInfo] = useState(false);
  // Brief amber flash on the qty readout when the user taps "−" at the
  // 3 yd minimum. Replaces the static "(min)" subtext — the digit itself
  // briefly turns amber to signal the floor without adding another line
  // of clutter under the number.
  const [flashMin, setFlashMin] = useState(false);
  const flashMinTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerMinFlash = () => {
    if (flashMinTimeoutRef.current) clearTimeout(flashMinTimeoutRef.current);
    setFlashMin(true);
    flashMinTimeoutRef.current = setTimeout(() => setFlashMin(false), 700);
  };
  useEffect(() => () => {
    if (flashMinTimeoutRef.current) clearTimeout(flashMinTimeoutRef.current);
  }, []);
  // Tactile click feedback for the +/- stepper pads: on tap, "+" briefly
  // flashes green and "−" flashes amber, then both ease back to the default
  // black/white after 0.5s.
  const [plusFlash, setPlusFlash] = useState(false);
  const [minusFlash, setMinusFlash] = useState(false);
  const plusFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const minusFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerPlusFlash = () => {
    if (plusFlashTimeoutRef.current) clearTimeout(plusFlashTimeoutRef.current);
    setPlusFlash(true);
    plusFlashTimeoutRef.current = setTimeout(() => setPlusFlash(false), 250);
  };
  const triggerMinusFlash = () => {
    if (minusFlashTimeoutRef.current) clearTimeout(minusFlashTimeoutRef.current);
    setMinusFlash(true);
    minusFlashTimeoutRef.current = setTimeout(() => setMinusFlash(false), 250);
  };
  useEffect(() => () => {
    if (plusFlashTimeoutRef.current) clearTimeout(plusFlashTimeoutRef.current);
    if (minusFlashTimeoutRef.current) clearTimeout(minusFlashTimeoutRef.current);
  }, []);
  // Address-first flow: address is the primary, always-visible input.
  // Once it geocodes successfully, material + quantity row reveals.
  const [addressZone, setAddressZone] = useState<{
    zone: number | null;
    fee: number;
    label: string;
  } | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [showMaterialRow, setShowMaterialRow] = useState(false);
  // Which second step the estimator shows once the address is confirmed.
  // false (default) = BROWSE: hand off to the materials catalog, where every
  // material has a photo, a "good for" line, and a price.
  // true = EXPRESS: the original material dropdown + quantity stepper, for
  // repeat buyers who already know the trade name of what they want.
  const [expressMode, setExpressMode] = useState(false);
  // First project in the guides hub — the "Browse by project" option deep-links
  // here, and the project page shows the full set of guides across the top.
  // Deduped in case the data file holds locale duplicates.
  const firstProjectSlug = Array.from(new Map(getProjects().map(p => [p.slug, p])).values())[0]?.slug || 'new-gravel-driveway';
  // Smooth-scroll to the materials catalog that sits directly below the hero,
  // offset by the sticky nav so the section header isn't hidden behind it.
  const scrollToCatalog = () => {
    const el = document.getElementById('materials');
    if (!el) return;
    const navHeight = document.querySelector('nav')?.offsetHeight || 80;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - navHeight,
      behavior: 'smooth'
    });
  };
  // True when the customer EXPLICITLY confirmed the address by picking a
  // "recent address" (which carries no coordinates). The debounced geocode
  // effect reads this and, once it resolves a valid delivery zone, opens the
  // material picker — the same outcome as picking a live suggestion. Without
  // this, a recent-address pick would confirm the zone pill but never open
  // materials, because the geocode effect otherwise stays silent to avoid
  // opening materials mid-typing.
  const pendingRecentConfirmRef = useRef(false);
  // True when the user explicitly chose "Continue anyway" after a failed
  // geocode. Quote is generated without a delivery fee; dispatch confirms
  // the address (and final fee) by phone before the truck rolls.
  const [addressUnverified, setAddressUnverified] = useState(false);
  // Custom yardage produced by the inline calculator (allows non-standard
  // single-trip quantities like 13yd, which auto-split across deliveries
  // in pricing math but display as a single line item in the quantity menu).
  const [customQty, setCustomQty] = useState<number | null>(null);
  // Manual yards input for the "More than 1 load" branch — lets users type
  // an exact yardage without going through the calculator.
  const [manualYards, setManualYards] = useState('');
  // Inline "Not sure?" calculator state for the How Much step
  const [showHeroCalc, setShowHeroCalc] = useState(false);
  const [heroCalcShape, setHeroCalcShape] = useState<'rectangle' | 'circle' | 'triangle' | 'irregular'>('rectangle');
  const [heroCalcLength, setHeroCalcLength] = useState('');
  const [heroCalcWidth, setHeroCalcWidth] = useState('');
  const [heroCalcDiameter, setHeroCalcDiameter] = useState('');
  const [heroCalcBase, setHeroCalcBase] = useState('');
  const [heroCalcHeight, setHeroCalcHeight] = useState('');
  const [heroCalcDepth, setHeroCalcDepth] = useState('3');
  const [showProjectsPanel, setShowProjectsPanel] = useState(false);
  // Bumped each time a material is freshly selected, so the Add-to-Truck
  // bright-green fill re-mounts and plays its one-shot 10yd → full-load reveal
  // (decelerating at the very end) instead of snapping straight to the
  // full-load width.
  const [loadBarRevealKey, setLoadBarRevealKey] = useState(0);
  const t = useT();
  const liveDeliveries = useLiveDeliveries();
  // Personalized greeting state — drives whether we replace the marketing
  // hero ("Spring Into Action / Fresh Starts Begin Here") with a
  // "Good morning, {firstName}" hero for returning visitors with a
  // delivered or in-flight order. Also tells us whether to suppress the
  // small WelcomeBackCard pill (only shown on the first return visit;
  // hidden after the personal greeting takes over).
  const personalGreeting = usePersonalGreeting();
  const showPersonalGreeting = personalGreeting.variant === 'in-flight' || personalGreeting.variant === 'repeat';
  const [searchParams, setSearchParams] = useSearchParams();
  // Build a "return to estimator" query string that preserves the user's
  // current estimator state (address, material, yardage). Used on every
  // outbound Link to /delivery-zones so the map page can offer a one-tap
  // "Back to Estimate" button — and Hero's mount-effect (below) will
  // re-hydrate the form from these same params on return.
  const estimatorReturnParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('from', 'estimator');
    if (address) params.set('address', address);
    if (material) params.set('material', material);
    const light = material && productMap[material] ? isLightweight(material) : false;
    const fullLoad = light ? 15 : 12;
    let yards = 0;
    if (quantity === 'multi') {
      const m = parseInt(manualYards, 10);
      if (!isNaN(m) && m > 0) {
        yards = m;
      } else if (numLoads === '5+') {
        // 5+ loads triggers the custom quote flow. For preserved-state
        // estimator return links (e.g. coming back from /delivery-zones)
        // we still need a yardage to round-trip, so estimate at the
        // bottom of the 5+ range.
        yards = 5 * fullLoad;
      } else if (numLoads) {
        const n = parseInt(numLoads, 10);
        if (!isNaN(n)) yards = n * fullLoad;
      }
    } else if (customQty && customQty > 0) {
      yards = customQty;
    } else if (quantity) {
      const n = parseInt(quantity, 10);
      if (!isNaN(n) && n > 0) yards = n;
    }
    if (yards > 0) params.set('qty', String(yards));
    return params.toString();
  }, [address, material, quantity, numLoads, manualYards, customQty]);
  // Return flow from Help Me Choose calculator: when the user comes back
  // with ?qty=N (and optionally &material=NAME&address=...), pre-fill the
  // estimator and scroll it into view so they can hit Get Estimate.
  // - qty ≤ full load → snap quantity dropdown to that single-load value
  // - qty > full load → switch to "Multiple loads" with the right load count
  useEffect(() => {
    const qtyParam = searchParams.get('qty');
    const materialParam = searchParams.get('material');
    const addressParam = searchParams.get('address');
    if (!qtyParam && !materialParam && !addressParam) return;
    if (addressParam) {
      setAddress(addressParam);
      // Returning with a confirmed address (e.g. "Back to Estimate" from the
      // delivery-zones map): make sure the estimator is engaged, and flag the
      // pending-confirm ref so the debounced geocode below re-reveals the
      // material window once it re-resolves the zone. Without this the address
      // round-trips back filled in but the material picker stays hidden with
      // no way to re-open it.
      setShowEstimator(true);
      pendingRecentConfirmRef.current = true;
    }
    if (materialParam && productMap[materialParam]) {
      setMaterial(materialParam);
    }
    if (qtyParam) {
      const n = parseInt(qtyParam, 10);
      if (!isNaN(n) && n >= 1) {
        // Determine full-load size from material if we have it, else assume
        // heavy (12 yd) — conservative default that splits more aggressively.
        const light = materialParam && productMap[materialParam] ? isLightweight(materialParam) : false;
        const fullLoad = light ? 15 : 12;
        if (n <= fullLoad) {
          // Snap to nearest valid single-load dropdown value (3..11 or fullLoad).
          const snapped = Math.max(3, Math.min(fullLoad, n));
          setQuantity(String(snapped));
          setNumLoads('');
        } else {
          // Multi-load: pick the load count that covers the requested yardage.
          // Loads 2-4 map to the corresponding dropdown option; 5 or more
          // falls into the custom-quote bucket.
          const loads = Math.ceil(n / fullLoad);
          setQuantity('multi');
          setNumLoads(loads >= 5 ? '5+' : String(loads));
        }
      }
    }
    // Clear the URL params after consuming them so a refresh doesn't
    // re-trigger this effect.
    const next = new URLSearchParams(searchParams);
    next.delete('qty');
    next.delete('material');
    next.delete('address');
    setSearchParams(next, {
      replace: true
    });
    // Scroll the estimator into view so the user lands on the pre-filled form.
    setTimeout(() => {
      estimatorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Seasonal messaging — rotates between 2 messages based on season, cycling on refresh
  const regionMeta = getRegionMeta();
  const springSummerMessages = [{
    headlineLine1: `${regionMeta.name}'s Gravel Delivery.`,
    headlineLine2: 'On Dispatch Today.',
    subtitle: 'The smartest way to order topsoil, bark mulch, and garden prep materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Build Season Is Here.',
    headlineLine2: 'Your Project Starts Now.',
    subtitle: 'The smartest way to order materials for driveways, patios, and landscaping — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: "Sun's Out, Trucks Out.",
    headlineLine2: "Let's Get It Done.",
    subtitle: 'The smartest way to order gravel, topsoil, and landscaping materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: "Your Yard Won't Wait.",
    headlineLine2: 'Neither Will We.',
    subtitle: 'The smartest way to order bark, compost, and garden-ready materials — instant estimates and automatic bulk pricing from your phone.'
  }];
  const fallWinterMessages = [{
    headlineLine1: `${regionMeta.name}'s Gravel Delivery.`,
    headlineLine2: 'On Dispatch Today.',
    subtitle: 'The smartest way to order drain rock, bark mulch, and grading materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'One Thumb.',
    headlineLine2: 'Full Driveway.',
    subtitle: 'The smartest way to order gravel, drainage, and winter prep materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Beat the Freeze.',
    headlineLine2: 'Gravel Up Before Winter.',
    subtitle: 'The smartest way to order crushed rock, drain rock, and fill materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Mud Season? Handled.',
    headlineLine2: 'Solid Ground Starts Here.',
    subtitle: 'The smartest way to order base rock, gravel, and drainage materials — instant estimates and automatic bulk pricing from your phone.'
  }];
  const [seasonalContent] = useState(() => {
    try {
      const month = new Date().getMonth(); // 0-11
      const isWarmSeason = month >= 2 && month <= 7; // Mar-Aug
      const pool = isWarmSeason ? springSummerMessages : fallWinterMessages;
      const key = 'hero_seasonal_index';
      const stored = parseInt(sessionStorage.getItem(key) || '0', 10);
      const index = stored % pool.length;
      sessionStorage.setItem(key, String(stored + 1));
      return pool[index];
    } catch {
      return fallWinterMessages[1];
    }
  });
  // Safety net: a SELECTED material must always keep its picker visible.
  // Several paths set `material` programmatically (returning from the
  // /delivery-zones map with ?material=, the Help-Me-Choose quiz, project
  // deep-links). In those cases the address geocode round-trip — which is the
  // path that normally flips `showMaterialRow` on — may not re-run or may
  // resolve out-of-zone, leaving the material box hidden with no way to
  // re-open it even though a material is already chosen. Keying the row open
  // off the presence of a valid material guarantees it can never disappear
  // once something is selected.
  useEffect(() => {
    if (material && productMap[material]) {
      setShowMaterialRow(true);
    }
  }, [material]);
  // Once the ADDRESS is verified and the material picker reveals, the page
  // slides so the BOTTOM edge of the estimator lines up with the bottom edge
  // of the mobile viewport — bringing the full flow (address → material →
  // quantity → Add to Truck) into view and surfacing the freshly-revealed
  // "What material?" step right above the phone's bottom edge.
  //
  // The slide itself fires from the Material row's `onAnimationComplete`
  // (below), NOT here — that callback runs only AFTER the row's
  // height: 0 → auto reveal has fully laid out, so the estimator card has
  // reached its final height and its measured bottom edge is correct.
  // A fixed timeout used to fire mid-animation against a still-collapsed
  // card, which is why the slide barely moved.
  // Debounced address geocoding — runs as user types. Once we get a valid zone,
  // we reveal the material + quantity row.
  useEffect(() => {
    if (!address.trim()) {
      setAddressZone(null);
      setAddressError('');
      // Don't collapse the material row if a material is already selected —
      // a chosen material keeps its picker open regardless of address state.
      if (!(material && productMap[material])) {
        setShowMaterialRow(false);
      }
      setAddressUnverified(false);
      pendingRecentConfirmRef.current = false;
      return;
    }
    // Don't geocode (and therefore don't flash the zone pill / out-of-zone
    // "custom rate" warning) until the typed address actually LOOKS like a
    // full street address. Mid-typing fragments like "123 M" used to resolve
    // to some far-off point and pop the out-of-zone box too eagerly. Require
    // a house number AND a few street-name letters AND enough total length
    // before we bother the geocoder. A pending recent-address confirmation
    // bypasses this gate (it's already a full, previously-used address).
    const q = address.trim();
    const looksComplete = q.length >= 8 && /\d/.test(q) && (q.match(/[a-zA-Z]/g)?.length ?? 0) >= 3;
    if (!pendingRecentConfirmRef.current && !looksComplete) {
      setAddressZone(null);
      setAddressError('');
      return;
    }
    if (addressUnverified) setAddressUnverified(false);
    const timer = setTimeout(async () => {
      setIsGeocoding(true);
      setAddressError('');
      try {
        // Only append ", WA" if the address doesn't already include the state —
        // doubling the state reference can cause Nominatim to return no results
        // for otherwise valid addresses (e.g. "...Seattle, WA 98107").
        const alreadyHasWA = /\b(WA|Washington)\b/i.test(address);
        const query = encodeURIComponent(alreadyHasWA ? address : address + ', WA');
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Try a more specific address.');
          setAddressZone(null);
          // Keep a chosen material visible even if this address can't be
          // resolved — the user can correct the address without losing it.
          if (!(material && productMap[material])) setShowMaterialRow(false);
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const info = getZoneInfo(lat, lon);
          setAddressZone(info);
          // Remember the verified, in-zone address so it reappears in the
          // "Recent addresses" dropdown next time. Only persist a real
          // delivery-zone match — out-of-zone geocodes aren't confirmed
          // deliverable yet.
          if (info.zone !== null) {
            pushRecentAddress(address.trim());
          }
          // Open the material picker ONLY when the customer explicitly
          // confirmed a "recent address" (which has no coordinates and so
          // must round-trip through this geocode). A recent-address pick is
          // an explicit confirmation — equivalent to picking a live
          // suggestion — so once it resolves to a valid delivery zone we
          // reveal materials. Plain mid-typing geocodes still leave the
          // picker closed; those open via onSelect / "Continue anyway".
          if (pendingRecentConfirmRef.current) {
            pendingRecentConfirmRef.current = false;
            // Reveal materials on a valid zone. If already selected, the
            // material effect keeps the row open regardless of zone result.
            if (info.zone !== null || material && productMap[material]) {
              setShowMaterialRow(true);
            } else {
              setShowMaterialRow(false);
            }
          }
        }
      } catch {
        setAddressError('Could not verify address. Please try again.');
        // Don't collapse a chosen material on a transient geocode error.
        if (!(material && productMap[material])) setShowMaterialRow(false);
      } finally {
        setIsGeocoding(false);
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, [address]);
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard against pressing Enter in the address field before a material
    // has been picked. The visible "Get Instant Estimate" button is gated
    // on address + material + quantity, but native form submit (Enter key)
    // would bypass that gate and produce a ghost quote using the fallback
    // $55/yd default — confusing the customer with a price for a material
    // they never selected.
    if (!address) return;
    if (!material || !productMap[material]) return;
    if (!quantity) return;
    if (quantity === 'multi' && !numLoads) return;
    setIsSearching(true);
    setQuoteResult(null);
    if (addressUnverified) {
      const productInfo = productMap[material];
      const basePrice = productInfo?.pricePerYard ?? 55;
      const light = isLightweight(material);
      const fullLoadYards = light ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '5+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (qty > fullLoadYards) {
          deliveries = Math.ceil(qty / fullLoadYards);
        } else {
          const threshold = light ? 12 : 10;
          isSmallLoad = qty < threshold;
          if (qty < threshold) {
            surchargeMultiplier = 1 + (threshold - qty) * 0.1;
          }
        }
      }
      // Manually-entered 1–2 yd loads are accepted as-is and priced with the
      // micro-load formula (1.0× material + flat $250 delivery). No bump to a
      // 3 yd minimum — we only reject tiny loads in the normal stepper flow.
      let billableQty = qty;
      let minAdjust: {
        requested: number;
        billable: number;
        lastLoadActual: number;
      } | null = null;
      const bulkPctU = billableQty >= fullLoadYards * 4 ? 0.1 : billableQty >= (light ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      let materialsTotal = 0;
      if (quantity !== 'multi' && deliveries > 1) {
        const lastLoad = qty - (deliveries - 1) * fullLoadYards;
        const threshold = light ? 12 : 10;
        let lastLoadSurcharge = 1;
        if (lastLoad < threshold) {
          lastLoadSurcharge = 1 + (threshold - lastLoad) * 0.1;
        }
        const fullLoadPrice = Math.ceil(basePrice * (1 - bulkPctU));
        const partialPrice = Math.ceil(basePrice * lastLoadSurcharge * (1 - bulkPctU));
        materialsTotal = (deliveries - 1) * fullLoadYards * fullLoadPrice + lastLoad * partialPrice;
      } else {
        const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPctU));
        materialsTotal = effective * billableQty;
      }
      const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPctU));
      setQuoteResult({
        priceLow: materialsTotal,
        priceHigh: materialsTotal,
        address,
        quantity: quantity === 'multi' ? String(qty) : String(billableQty),
        material,
        materialSlug: productInfo?.slug,
        deliveryZone: null,
        deliveryFee: 0,
        deliveryLabel: "Delivery fee TBD — we'll confirm before dispatch",
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effective,
        effectivePricePerYardHigh: effective,
        basePricePerYardLow: basePrice,
        basePricePerYardHigh: basePrice,
        deliveries,
        bulkDiscount: bulkPctU,
        minLoadAdjustment: minAdjust
      });
      setIsSearching(false);
      setTimeout(() => {
        if (quoteResultRef.current) {
          const navHeight = document.querySelector('nav')?.offsetHeight || 80;
          const top = quoteResultRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 12;
          window.scrollTo({
            top: Math.max(0, top),
            behavior: 'smooth'
          });
        }
      }, 250);
      return;
    }
    try {
      // Geocode the address — only append ", WA" if the state isn't already
      // in the user's input, to avoid Nominatim zero-result quirks on doubled
      // state references like "...Seattle, WA 98107".
      const alreadyHasWA = /\b(WA|Washington)\b/i.test(address);
      const query = encodeURIComponent(alreadyHasWA ? address : address + ', WA');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
      const data = await res.json();
      let zoneInfo = {
        zone: null as number | null,
        fee: 0,
        label: 'Custom Quote'
      };
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        zoneInfo = getZoneInfo(lat, lon);
      }
      const productInfo = productMap[material];
      const basePrice = productInfo?.pricePerYard ?? 55;
      const light = isLightweight(material);
      const fullLoadYards = light ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '5+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (qty > fullLoadYards) {
          deliveries = Math.ceil(qty / fullLoadYards);
          isSmallLoad = false;
          surchargeMultiplier = 1;
        } else {
          const threshold = 10;
          isSmallLoad = qty < threshold;
          if (qty < 3) {
            surchargeMultiplier = 1;
          } else if (qty >= threshold) {
            surchargeMultiplier = 1;
          } else {
            surchargeMultiplier = 1 + (threshold - qty) * 0.1;
          }
        }
      }
      let billableQty = qty;
      let minAdjust = null;
      const bulkPct = billableQty >= fullLoadYards * 4 ? 0.1 : billableQty >= (light ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      let materialsTotal = 0;
      let totalDeliveryFee = 0;
      if (quantity !== 'multi' && deliveries > 1) {
        const lastLoad = qty - (deliveries - 1) * fullLoadYards;
        const threshold = 10;
        let lastLoadSurcharge = 1;
        if (lastLoad >= 3 && lastLoad < threshold) {
          lastLoadSurcharge = 1 + (threshold - lastLoad) * 0.1;
        }
        const fullLoadPrice = Math.ceil(basePrice * (1 - bulkPct));
        const partialPrice = Math.ceil(basePrice * lastLoadSurcharge * (1 - bulkPct));
        materialsTotal = (deliveries - 1) * fullLoadYards * fullLoadPrice + lastLoad * partialPrice;
        totalDeliveryFee = (deliveries - 1) * zoneInfo.fee + (lastLoad < 3 ? 250 : zoneInfo.fee);
      } else {
        const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct));
        materialsTotal = effective * billableQty;
        totalDeliveryFee = qty < 3 ? 250 : zoneInfo.fee;
      }
      const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct));
      const total = materialsTotal + totalDeliveryFee;
      setQuoteResult({
        priceLow: total,
        priceHigh: total,
        address,
        quantity: quantity === 'multi' ? String(qty) : String(billableQty),
        material,
        materialSlug: productInfo?.slug,
        deliveryZone: zoneInfo.zone,
        deliveryFee: totalDeliveryFee,
        deliveryLabel: zoneInfo.label,
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effective,
        effectivePricePerYardHigh: effective,
        basePricePerYardLow: basePrice,
        basePricePerYardHigh: basePrice,
        deliveries,
        bulkDiscount: bulkPct,
        minLoadAdjustment: minAdjust
      });
    } catch {
      const productInfo = productMap[material];
      const basePrice = productInfo?.pricePerYard ?? 55;
      const light = isLightweight(material);
      const fullLoadYards = light ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '5+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (qty > fullLoadYards) {
          deliveries = Math.ceil(qty / fullLoadYards);
          isSmallLoad = false;
          surchargeMultiplier = 1;
        } else {
          const threshold = light ? 12 : 10;
          isSmallLoad = qty < threshold;
          if (qty >= threshold) {
            surchargeMultiplier = 1;
          } else {
            surchargeMultiplier = 1 + (threshold - qty) * 0.1;
          }
        }
      }
      // Manually-entered 1–2 yd loads are accepted as-is and priced with the
      // micro-load formula (1.0× material + flat $250 delivery). No bump to a
      // 3 yd minimum — we only reject tiny loads in the normal stepper flow.
      let billableQty = qty;
      let minAdjust: {
        requested: number;
        billable: number;
        lastLoadActual: number;
      } | null = null;
      const bulkPct2 = billableQty >= fullLoadYards * 4 ? 0.1 : billableQty >= (light ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      let materialsTotal = 0;
      if (quantity !== 'multi' && deliveries > 1) {
        const lastLoad = qty - (deliveries - 1) * fullLoadYards;
        const threshold = light ? 12 : 10;
        let lastLoadSurcharge = 1;
        if (lastLoad < threshold) {
          lastLoadSurcharge = 1 + (threshold - lastLoad) * 0.1;
        }
        const fullLoadPrice = Math.ceil(basePrice * (1 - bulkPct2));
        const partialPrice = Math.ceil(basePrice * lastLoadSurcharge * (1 - bulkPct2));
        materialsTotal = (deliveries - 1) * fullLoadYards * fullLoadPrice + lastLoad * partialPrice;
      } else {
        const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct2));
        materialsTotal = effective * billableQty;
      }
      const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct2));
      const total = materialsTotal + 100 * deliveries;
      setQuoteResult({
        priceLow: total,
        priceHigh: total,
        address,
        quantity: quantity === 'multi' ? String(qty) : String(billableQty),
        material,
        materialSlug: productInfo?.slug,
        deliveryZone: null,
        deliveryFee: 0,
        deliveryLabel: 'Custom Quote',
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effective,
        effectivePricePerYardHigh: effective,
        basePricePerYardLow: basePrice,
        basePricePerYardHigh: basePrice,
        deliveries,
        bulkDiscount: bulkPct2,
        minLoadAdjustment: minAdjust
      });
    }
    setIsSearching(false);
    setTimeout(() => {
      if (quoteResultRef.current) {
        const navHeight = document.querySelector('nav')?.offsetHeight || 80;
        const top = quoteResultRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      }
    }, 250);
  };
  const handleCloseEstimator = () => {
    setShowEstimator(false);
    setShowMaterialRow(false);
    setMaterial('');
    setQuantity('12');
    setNumLoads('');
    setQuoteResult(null);
    setAddress('');
    setAddressZone(null);
    setAddressError('');
  };
  // When an order is placed (from the estimator's "Add to truck" flow or the
  // cart), reset the estimator back to its default collapsed state. The cart
  // drawer fires an 'estimatorReset' window event on a successful order.
  useEffect(() => {
    const onOrderPlaced = () => handleCloseEstimator();
    window.addEventListener('estimatorReset', onOrderPlaced);
    return () => window.removeEventListener('estimatorReset', onOrderPlaced);
  }, []);
  // One-tap: add the quoted material to the cart, persist delivery info, open cart drawer.
  // Lets a finished estimator flow straight into checkout without re-entering address.
  // For multi-load orders, split into separate cart line items so the user sees
  // a clear breakdown (and the small-load surcharge applies correctly to the
  // partial last load).
  const handleAddToTruckFromQuote = () => {
    if (!quoteResult) return;
    const productInfo = productMap[quoteResult.material];
    if (!productInfo) return;
    // For multi-delivery orders the billed quantity equals what the
    // customer asked for (partial last loads no longer get bumped). For a
    // standalone <5 yd single delivery we still want to add 5 yd to the
    // cart (billing minimum) and flag requestedQuantity so the cart can
    // show the transparency note.
    const finalQuantity = parseInt(quoteResult.quantity) || quoteResult.minLoadAdjustment?.billable || 0;
    if (finalQuantity <= 0) return;
    const deliveries = quoteResult.deliveries || 1;
    const light = isLightweight(quoteResult.material);
    const fullLoad = light ? 15 : 12;
    if (deliveries > 1) {
      // Split across N deliveries: full loads for all but last, actual
      // remainder for the last. Each load is its own cart line item so the
      // user sees the breakdown and the small-load surcharge applies
      // naturally to a small partial last load (no 5 yd minimum bump on
      // partial loads — customer is already paying for at least one full
      // load).
      const firstLoadsTotal = (deliveries - 1) * fullLoad;
      const lastLoad = finalQuantity - firstLoadsTotal;
      for (let i = 0; i < deliveries; i++) {
        const isLast = i === deliveries - 1;
        const qty = isLast ? lastLoad : fullLoad;
        addToCart({
          category: productInfo.categoryName,
          product: `${quoteResult.material} (Delivery ${i + 1})`,
          quantity: qty,
          pricePerYard: productInfo.pricePerYard,
          source: 'estimator'
        });
      }
    } else {
      // Standalone single-delivery: flag requestedQuantity when we bumped
      // a <5 yd order up to the 5 yd order minimum so the cart can render
      // the transparency note.
      const adj = quoteResult.minLoadAdjustment;
      const requestedQuantity = adj && adj.requested > 0 && adj.requested < finalQuantity ? adj.requested : undefined;
      addToCart({
        category: productInfo.categoryName,
        product: quoteResult.material,
        quantity: finalQuantity,
        pricePerYard: productInfo.pricePerYard,
        source: 'estimator',
        requestedQuantity
      });
    }
    // Persist the address + zone + fee so CartDrawer doesn't ask again.
    setDeliveryInfo(quoteResult.address, quoteResult.deliveryFee && quoteResult.deliveries ? Math.round(quoteResult.deliveryFee / quoteResult.deliveries) : 0, quoteResult.deliveryZone);
    openCart();
  };
  return <section ref={heroSectionRef} className="relative w-full bg-[#fbfbfb] dark:bg-[#2a2a2a] py-14 md:py-40 overflow-hidden" style={{
    marginTop: '-149px',
    paddingTop: 'calc(149px + 3.5rem)'
  }} data-id="element-864" data-mp="39wfckw6i3w8r">
      {/* Background Image - extends behind navbar */}
      <motion.div className="absolute inset-0 z-0 bg-cover bg-center" style={{
      backgroundImage: `url(${heroImage})`,
      top: 0
    }} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1.5,
      ease: 'easeInOut'
    }} data-id="element-865" data-mp="3eugwfjbr3zla" />
      {/* Black overlays — same in light & dark mode */}
      <div className="absolute inset-0 z-0 md:hidden bg-black/50" data-id="element-866" data-mp="2zumk1dsxnqrp" />
      <div className="absolute inset-0 z-0 hidden md:block bg-gradient-to-r from-black/70 via-black/40 to-black/10" data-id="element-867" data-mp="34rcyjwp3aeb0" />

      {/* The photo stays behind the estimator at all times. It used to fade out
          to a flat backdrop when the estimator opened, which left the hero and
          the Materials Hub below it sharing nearly the same surface with no
          edge between them. Keeping the photo gives the section a hard boundary
          and lets the address field stay immediately typeable on load.
          (Former "bring up the lights" wash removed.) */}


      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-868" data-mp="dwv6rbu430r">
        {/* Small WelcomeBackCard pill — only renders on the FIRST return
            visit after a delivered order (the 'just-delivered-once'
            variant). Once the user is on their 2nd+ return visit, the
            big PersonalGreeting takes over the hero slot entirely and
            this small pill steps aside to avoid duplicating the same
            affordance twice on screen. */}
        {!showPersonalGreeting && <WelcomeBackCard data-id="element-869" data-mp="ahlfg9vpvmea" />}

        <div className="text-center md:text-left mb-8 md:mb-12 max-w-3xl" data-id="element-870" data-mp="aiwiu8l92s4v">
          {showPersonalGreeting ? <PersonalGreeting variant={personalGreeting.variant} firstName={personalGreeting.firstName} savedOrder={personalGreeting.savedOrder} activeOrder={personalGreeting.activeOrder} activeStepIdx={personalGreeting.activeStepIdx} estimatorOpen={showEstimator} isDark={isDark} data-id="element-871" data-mp="6hbb11nkp41j" /> : <>
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20
          }} className="flex items-center justify-center md:justify-start mt-6 md:mt-10 mb-4 md:mb-6" data-id="element-872" data-mp="tfxqk166v6ci">
                <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-none text-xs sm:text-sm font-semibold tracking-widest uppercase text-white font-[Inter,_sans-serif] ${isDark ? 'bg-[#006239]' : 'bg-[#00b182]'}`} data-id="element-873" data-mp="1h479mvc0pfzy">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" data-id="element-874" data-mp="31ovtpdevjch4" />
                  Order Gravel Like It's 2026
                </span>
              </motion.div>

              <AnimatePresence mode="wait" data-id="element-875" data-mp="2snny533qspqq">
                <motion.div initial={{
              opacity: 0,
              scale: 0.95,
              y: 20
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 20,
              delay: 0.1
            }} data-id="element-876" data-mp="2smuyem37b0i3">
                  <AnimatePresence mode="wait" data-id="element-877" data-mp="gyd8i0j4j36t">
                    <motion.h1 key={seasonalContent.headlineLine1} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.3
                }} className={cn(`font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 md:mb-6 leading-[1.1] ${showEstimator ? isDark ? 'text-white' : 'text-stone-900' : 'text-stone-900 dark:text-white'}`, "font-[900]")} data-id="element-878" data-mp="gymwxjoit2ae">
                      {seasonalContent.headlineLine1}
                      <span className="block mt-1 md:mt-2 italic font-medium tracking-normal" style={{
                    fontFamily: '"Playfair Display", serif'
                  }} data-id="element-879" data-mp="2bdk2w6lwjtgr">
                        {seasonalContent.headlineLine2}
                      </span>
                    </motion.h1>
                  </AnimatePresence>
                </motion.div>
              </AnimatePresence>

              <AnimatePresence mode="wait" data-id="element-880" data-mp="2u4bihpwhgg3l">
                <motion.p key={seasonalContent.subtitle} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3,
              delay: 0.1
            }} className={`text-lg md:text-xl font-semibold font-sub mb-5 md:mb-6 max-w-2xl mx-auto md:mx-0 ${showEstimator ? isDark ? 'text-white/90' : 'text-stone-700' : 'text-stone-700 dark:text-white/90'}`} data-id="element-881" data-mp="2u5cwo38spipj">
                  {seasonalContent.subtitle}
                </motion.p>
              </AnimatePresence>
            </>}

          {/* Review Badge — only while the estimator is CLOSED. With the
              estimator open it moves to the bottom of the panel (see below), so
              the social proof also acts as a closing note and puts some air
              between the hero and the materials section underneath. */}
          {!showEstimator && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.25
        }} className="mb-3 md:mb-5 space-y-2" data-id="element-882" data-mp="9xmb7pv9n9x0">
              <div className="flex items-center gap-2 justify-center md:justify-start" data-id="element-883" data-mp="35spkkotwlgcn">
                <div className="flex items-center gap-2.5" data-id="element-884" data-mp="30vgxc9twem4j">
                  <div className="flex" data-id="element-885" data-mp="2w3605eqjlefo">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-stone-700 fill-stone-700 dark:text-white/85 dark:fill-white/85" data-id="element-886" data-mp="2w1ctw0jucw87" data-mpi={i} />)}
                  </div>
                  <span className="text-sm font-medium tabular-nums text-stone-900 dark:text-white/85" data-id="element-887" data-mp="2m5i0qbhzrir6">
                    {liveDeliveries.toLocaleString()}+ {t('hero.deliveriesWord')}
                  </span>
                </div>
              </div>
            </motion.div>}

          {/* CTA Buttons */}
          {!showEstimator && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.3
        }} className="flex flex-col items-center md:items-start gap-3" data-id="element-888" data-mp="2871lfw62cjxf">
              <button onClick={() => {
            // Activate the estimator, then slide the page so the live
            // order ticker bar sits 40px above the bottom of the
            // viewport.
            setShowEstimator(true);
            setTimeout(slideToTicker, 200);
          }} className="estimate-pulse-dark group block whitespace-nowrap text-center md:text-left py-3 md:py-4 my-5 font-heading text-[clamp(1.75rem,8vw,5rem)] leading-[1.05] font-extrabold tracking-tight" data-id="element-889" data-mp="27rsg5atjgad4">
                <span className="underline decoration-current decoration-[6px] sm:decoration-[8px] underline-offset-[10px] sm:underline-offset-[14px] text-[#009C55] group-hover:text-[#21e169] transition-colors duration-100" data-id="element-890" data-mp="2ml418jhd4h2d">
                  {t('hero.getEstimate')}
                </span>
              </button>
            </motion.div>}
        </div>
        {/* Estimator Card */}
        <AnimatePresence data-id="element-891" data-mp="118aozjeyv6gj">
          {showEstimator && <motion.div initial={{
          opacity: 0,
          y: 40,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} ref={estimatorRef} className={`relative z-20 mb-10 max-w-[800px] mx-auto md:mx-0 scroll-mt-[180px] pt-2`} data-id="element-892" data-mp="r5yqoasb6mvh">
              {/* No close (X) here anymore: the estimator no longer swaps the
               hero to a different surface, so there is no visual "mode" to
               escape from. The "Start over" reset at the bottom still clears an
               in-progress quote. */}

              {/* space-y-[27px] = 2rem (space-y-8) reduced ~15% — tightens the
               vertical gap between the Address, Material, and Quantity steps. */}
              <form onSubmit={handleSearch} className="space-y-[27px]" data-id="element-893" data-mp="3qkso463pyewt">
                {/* Step 1: Address (always first) */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-2.5" data-id="element-894" data-mp="3ilfmdwhf24on">
                  <label className={cn(`block font-heading text-2xl font-bold tracking-tight sm:text-3xl ${isDark ? 'text-white/80' : 'text-black/75'}`, "text-[24px]", "text-[24px]")} style={{
                fontSize: "24px"
              }} data-id="element-895" data-mp="3sjlhnv8pwckj">
                    Where are we delivering?
                  </label>
                  <AddressAutocomplete value={address} onChange={next => {
                setAddress(next);
                // Typing/editing the address means it is no longer a
                // confirmed delivery location — re-gate the material
                // picker so it stays closed until the customer
                // explicitly confirms the new address (picks a
                // suggestion or clicks "Continue anyway"). Without this,
                // a previously-confirmed address that got edited would
                // leave materials open against an unverified address.
                setShowMaterialRow(false);
                // If the user edits the address after we've already
                // produced a quote, invalidate the quote — otherwise
                // clicking "Add to Cart" would push the OLD address
                // (the one tied to the prior quote) into the cart
                // instead of what the customer just typed. Forcing
                // a re-quote guarantees the cart and the estimator
                // always agree on the delivery address.
                if (quoteResult && next !== quoteResult.address) {
                  setQuoteResult(null);
                }
              }} onSelect={s => {
                // User picked a suggestion — skip the debounced geocode
                // and act on the coordinates directly.
                const info = getZoneInfo(s.lat, s.lon);
                setAddressZone(info);
                setAddressError('');
                // Remember this confirmed address so it reappears in the
                // "Recent addresses" dropdown on the next visit.
                pushRecentAddress(s.fullAddress);
                setRecentAddrTick(n => n + 1);
                // Only open materials when the picked address is verified
                // inside a delivery zone. An out-of-zone pick keeps
                // materials closed until the customer acknowledges the
                // "custom rate / can't verify" warning box.
                setShowMaterialRow(info.zone !== null);
                // Suggestion-pick is also an address change, so any
                // existing quote is now stale — clear it so the user
                // re-runs the estimate against the new address.
                if (quoteResult && s.fullAddress !== quoteResult.address) {
                  setQuoteResult(null);
                }
              }} onSelectRecent={() => {
                // A recent address has no coordinates, so it must
                // re-geocode. Flag it as an explicit confirmation so
                // the debounced geocode effect opens the material
                // picker once it resolves a valid delivery zone.
                pendingRecentConfirmRef.current = true;
              }} placeholder="Start typing your address" required autoComplete="off-mp-estimator-address" rightIcon={!isGeocoding && addressZone?.zone ? <CheckCircle2 className={`w-6 h-6 ${isDark ? 'text-white' : 'text-[#00704A]'}`} data-id="element-897" data-mp="1uo6d2sa2egcc" /> : undefined} isLoading={isGeocoding} variant="light" recentAddresses={recentAddresses} containerClassName={isDark ? 'estimator-addr-wrap--dark' : 'estimator-addr-wrap--light'}
              // Underline field rather than a filled box: the address is the
              // opening question of the whole flow, and a rule reads as an
              // invitation to type where a heavy input reads as a form.
              inputClassName={`estimator-underline-input ${isDark ? 'estimator-underline-input--dark' : 'estimator-underline-input--light'} block w-full bg-transparent pl-0 pr-14 h-[68px] sm:h-[80px] rounded-none border-0 border-b-2 text-xl sm:text-2xl font-medium focus:outline-none focus:ring-0 transition-colors truncate ${isDark ? 'border-white/25 text-white placeholder:text-white/45 focus:border-[#04A05C]' : 'border-black/80 text-stone-900 placeholder:text-stone-400 focus:border-[#006239]'}`} data-id="element-896" data-mp="fjcvhfyrf99" />
                  <AnimatePresence mode="wait" data-id="element-898" data-mp="119c0wi73egsz">
                    {addressError && !isGeocoding && !addressUnverified && <motion.div key="err" initial={{
                  opacity: 0,
                  y: -4,
                  height: 0
                }} animate={{
                  opacity: 1,
                  y: 0,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-899" data-mp="x91y1eelk6ic">
                        <div className="mt-1 rounded-lg border border-amber-400/50 bg-amber-400/10 p-3.5" data-id="element-900" data-mp="2p61qq195b8p5">
                          <div className="flex items-start gap-2.5" data-id="element-901" data-mp="2txjq4kvit180">
                            <Info className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-902" data-mp="2g5vk8zbv78zh" />
                            <div className="flex-1 min-w-0" data-id="element-903" data-mp="2l46rd2arz5e0">
                              <p className={`text-sm font-bold leading-snug ${isDark ? 'text-amber-200' : 'text-amber-800'}`} data-id="element-904" data-mp="2l2lw1agmzif3">
                                We couldn't auto-verify this address
                              </p>
                              <p className={`mt-1 text-xs leading-relaxed ${isDark ? 'text-white/80' : 'text-stone-700'}`} data-id="element-905" data-mp="2ywwfpdds0mea">
                                No problem — you can still get a quote for the
                                materials. Our dispatcher will reach out to
                                confirm the delivery address and final fee
                                before your truck is scheduled.
                              </p>
                              <div className="mt-3 flex flex-col sm:flex-row gap-2" data-id="element-906" data-mp="2k56y7id9by47">
                                <a href="tel:206-619-3742" className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-white hover:bg-stone-100 text-stone-900 text-xs font-bold transition-colors" data-id="element-907" data-mp="2f37to55s6v5o">
                                  <Phone className="w-3.5 h-3.5 text-[#059F40]" data-id="element-908" data-mp="258poswomotka" />
                                  Call dispatch · (206) 619-3742
                                </a>
                                <button type="button" onClick={() => {
                            setAddressUnverified(true);
                            setAddressError('');
                            setAddressZone(null);
                            setShowMaterialRow(true);
                          }} className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-[#059F40] hover:bg-[#048036] text-white text-xs font-bold transition-colors" data-id="element-909" data-mp="2xvsfj2n2bzzz">
                                  Continue anyway
                                  <ArrowRight className="w-3.5 h-3.5" data-id="element-910" data-mp="3p5554t3okcwu" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                    {addressUnverified && !isGeocoding && <motion.div key="unverified" initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-2 text-xs font-medium" data-id="element-911" data-mp="bw8ti1oxbjxh">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/15 border font-bold ${isDark ? 'border-amber-400/40 text-amber-300' : 'border-amber-500/50 text-amber-800'}`} data-id="element-912" data-mp="pnx420jiy0cc">
                          <Info className="w-3 h-3" data-id="element-913" data-mp="zmcus2en37lb" />
                          Address pending confirmation
                        </span>
                        <span className="text-stone-700 dark:text-white/70" data-id="element-914" data-mp="26ckguzrq8gl7">
                          Dispatch will confirm by phone before delivery
                        </span>
                      </motion.div>}
                    {addressZone && addressZone.zone !== null && !isGeocoding && <motion.div key="zone" initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-2 text-xs font-medium" data-id="element-915" data-mp="1weo5c6uvnjmk">
                          {(() => {
                    // Pull the per-zone color so the pill mirrors
                    // the color the customer would see for their
                    // zone on the delivery map. Inline styles
                    // here because Tailwind can't safely build
                    // arbitrary runtime classes. Fee is folded
                    // INTO the pill so the whole thing reads as
                    // one tappable affordance.
                    const zoneColor = ZONE_COLORS[addressZone.zone as number] || '#059F40';
                    const feeLabel = addressZone.fee === 0 ? t('hero.estimator.freeDelivery') : `+${addressZone.fee} ${t('hero.estimator.plusDelivery')}`;
                    return <Link to={`/delivery-zones?${estimatorReturnParams}`} className="group inline-flex items-center gap-1.5 rounded-md py-[6px] bg-transparent uppercase tracking-wide transition-colors hover:opacity-80 text-[12px] font-[700] font-[Cabin,_sans-serif] text-[#f59e0b]" title="View on delivery zones map" data-id="element-916" data-mp="df6vchqi2joh">
                                <MapPin className="w-3.5 h-3.5" data-id="element-917" data-mp="ie045wuudmib" />
                                {t('hero.quote.zone')} {addressZone.zone}
                                <span className="opacity-60" data-id="element-918" data-mp="1yuw02qlz5qrd">·</span>
                                {feeLabel}
                              </Link>;
                  })()}
                        </motion.div>}
                    {addressZone && addressZone.zone === null && !addressUnverified && !isGeocoding && <motion.div key="custom" initial={{
                  opacity: 0,
                  y: -4,
                  height: 0
                }} animate={{
                  opacity: 1,
                  y: 0,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-919" data-mp="25ycb7apkcs94">
                          {/* Out-of-zone address — geocoded fine but lands
                           outside our standard delivery zones. Treated as
                           "can't be auto-verified": materials stay closed
                           until the customer taps "Continue anyway" to
                           acknowledge the custom-rate quote path. */}
                          <div className="mt-1 rounded-lg border border-amber-400/50 bg-amber-400/10 p-3.5" data-id="element-920" data-mp="3hp9zajzo2g0z">
                            <div className="flex items-start gap-2.5" data-id="element-921" data-mp="3dnf31twlesu2">
                              <Info className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-922" data-mp="3dm3ttekdosvk" />
                              <div className="flex-1 min-w-0" data-id="element-923" data-mp="33qaddk3jgf8i">
                                <p className={`text-sm font-bold leading-snug ${isDark ? 'text-amber-200' : 'text-amber-800'}`} data-id="element-924" data-mp="33rva9gdbsa9j">
                                  {t('hero.estimator.outsideZones')}
                                </p>
                                <p className={`mt-1 text-xs leading-relaxed ${isDark ? 'text-white/80' : 'text-stone-700'}`} data-id="element-925" data-mp="2pxui55gvpli5">
                                  We can still deliver here — our dispatcher
                                  will confirm the exact delivery rate by phone
                                  before your truck is scheduled.
                                </p>
                                <div className="mt-3 flex flex-col sm:flex-row gap-2" data-id="element-926" data-mp="ey8fqzphrsm3">
                                  <a href="tel:206-619-3742" className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-white hover:bg-stone-100 text-stone-900 text-xs font-bold transition-colors" data-id="element-927" data-mp="f0b65kuk3nrc">
                                    <Phone className="w-3.5 h-3.5 text-[#059F40]" data-id="element-928" data-mp="a6qd877tljz0" />
                                    Call dispatch · (206) 619-3742
                                  </a>
                                  <button type="button" onClick={() => {
                            setAddressUnverified(true);
                            setShowMaterialRow(true);
                          }} className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-[#059F40] hover:bg-[#048036] text-white text-xs font-bold transition-colors" data-id="element-929" data-mp="16ae19y40yzy">
                                    Continue anyway
                                    <ArrowRight className="w-3.5 h-3.5" data-id="element-930" data-mp="1lmncopdabmah" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>}
                  </AnimatePresence>
                </motion.div>

                {/* Step 2 — BROWSE path (default). Once the address is
                    confirmed, the natural next step used to be a 28-item text
                    dropdown, which assumes the customer already recognizes trade
                    names like "5/8 minus". Instead we hand them to the catalog
                    below, where every material has a photo, a "good for" line,
                    and a price. Express mode (below) restores the dropdown for
                    customers who already know what they want. */}
                <AnimatePresence data-id="element-931" data-mp="24ekj5gk5fo0u">
                  {showMaterialRow && !expressMode && <motion.div initial={{
                opacity: 0,
                height: 0,
                y: -10
              }} animate={{
                opacity: 1,
                height: 'auto',
                y: 0
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35
              }} onAnimationComplete={slideToTicker} className="space-y-7 overflow-visible" data-id="element-932" data-mp="1zfzff75oum7n">
                      {[{
                  key: 'materials',
                  heading: 'Pick your materials',
                  link: 'Browse materials',
                  onClick: scrollToCatalog
                }, {
                  key: 'express',
                  heading: 'Know what you want?',
                  link: 'Express checkout',
                  onClick: () => setExpressMode(true)
                }, {
                  key: 'project',
                  heading: 'Browse by project',
                  link: 'See project guides',
                  to: `/projects/${firstProjectSlug}`
                }].map(option => <div key={option.key} data-id="element-933" data-mp="19z715evtb2pq">
                          {/* The label is the quiet part — the action is what
                           the customer is choosing between, so it carries the
                           weight. */}
                          <p className={`font-sub text-xs font-semibold uppercase tracking-[0.14em] ${isDark ? 'text-white/55' : 'text-black/50'}`} data-id="element-934" data-mp="1a6ita3zssgam">
                            {option.heading}
                          </p>
                          {/* These read at the same weight/size/color as the
                           address input above, so the three paths feel like
                           the natural continuation of the same field rather
                           than small secondary links. */}
                          {option.to ? <Link to={option.to} className="group mt-1 inline-flex items-center gap-2.5 font-heading text-3xl font-bold tracking-tight text-white transition-colors hover:text-[#04A05C] sm:text-4xl" data-id="element-935" data-mp="1p3jh3wevbr3t">
                              {option.link}
                              <ArrowRight className="w-6 h-6 shrink-0 transition-transform group-hover:translate-x-1" data-id="element-936" data-mp="30jsw5sdaayho" />
                            </Link> : <button type="button" onClick={option.onClick} className="group mt-1 inline-flex items-center gap-2.5 font-heading text-3xl font-bold tracking-tight text-white transition-colors hover:text-[#04A05C] sm:text-4xl" data-id="element-937" data-mp="35baxwin4t33p">
                              {option.link}
                              <ArrowRight className="w-6 h-6 shrink-0 transition-transform group-hover:translate-x-1" data-id="element-938" data-mp="3j9r7qigaj9q2" />
                            </button>}
                        </div>)}
                    </motion.div>}
                </AnimatePresence>

                {/* Step 2 — EXPRESS path: the original material dropdown. */}
                <AnimatePresence data-id="element-939" data-mp="3e9cy4vz1jrpx">
                  {showMaterialRow && expressMode && <motion.div initial={{
                opacity: 0,
                height: 0,
                y: -10
              }} animate={{
                opacity: 1,
                height: 'auto',
                y: 0
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35
              }} onAnimationComplete={() => {
                // Address just confirmed and the "What material?" row
                // has finished revealing. At THIS stage (no material
                // picked yet) we slide up only until the live order
                // ticker bar comes fully into view — NOT all the way to
                // align the short estimator's bottom with the screen.
                // Once a material is picked, the quantity row's own
                // onAnimationComplete takes over and slides the now-
                // taller estimator flush to the viewport bottom.
                if (!(material && productMap[material])) {
                  slideToTicker();
                }
              }} className="space-y-2 overflow-visible" data-id="element-940" data-mp="1xwelqoyeeh1a">
                      <label className={cn(`mt-3 mb-2.5 block font-heading text-2xl font-bold tracking-tight sm:text-3xl ${isDark ? 'text-white/80' : 'text-black/70'}`, "text-[24px]")} style={{
                  fontSize: "24px"
                }} data-id="element-941" data-mp="3aldgt56aw50q">
                        What material?
                      </label>
                      <CustomSelect value={material} onChange={val => {
                  setMaterial(val);
                  // Auto-bump the quantity dropdown to the per-product
                  // default when material changes. Soil/Mulch/Org.
                  // products (except 50/50 Mix) default to 15 yd —
                  // a full light-material truckload and the typical
                  // homeowner spring-project size. Everything else
                  // (and 50/50 Mix) stays on 12 yd.
                  setQuantity(getDefaultQuantity(val));
                  // Replay the Add-to-Truck load-bar reveal: the bright
                  // fill starts at the 10yd mark and eases up to the
                  // default full load, slowing at the very end.
                  setLoadBarRevealKey(k => k + 1);
                }} placeholder="Select Material" options={materialOptions} isDark={isDark} data-id="element-942" data-mp="1f530td20mbqr" />
                      <p className={`text-xs pt-1 leading-relaxed font-[Manrope,_sans-serif] ${isDark ? 'text-white/60' : 'text-stone-600'}`} data-id="element-943" data-mp="2x9rwlfteutq">
                        Not sure what you need?{' '}
                        <button type="button" onClick={() => {
                    setExpressMode(false);
                    setMaterial('');
                  }} className="font-semibold hover:underline whitespace-nowrap text-[#00905B]" data-id="element-944" data-mp="cvfruxi29qvy">
                          Browse materials with photos →
                        </button>
                      </p>
                    </motion.div>}
                </AnimatePresence>

                {/* Step 3: How much — only reveals after a material is picked */}
                <AnimatePresence data-id="element-945" data-mp="19hj4iuu9sg2v">
                  {showMaterialRow && material && <motion.div initial={{
                opacity: 0,
                height: 0,
                y: -10
              }} animate={{
                opacity: 1,
                height: 'auto',
                y: 0
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35,
                delay: 0.05
              }} onAnimationComplete={() => {
                // Picking a material reveals this quantity + price +
                // Add-to-Truck block, which grows the estimator card
                // taller. The Material row's onAnimationComplete only
                // fires once (on first reveal after address confirm),
                // so we re-run the slide HERE once this row has fully
                // laid out — keeping the bottom of the now-taller
                // estimator flush with the bottom of the phone.
                slideEstimatorBottomToViewport();
              }} className="space-y-2 overflow-visible" data-id="element-946" data-mp="14ltz4avkk4yn">
                      <label className={cn(`mt-3 mb-2.5 block font-heading text-2xl font-bold tracking-tight sm:text-3xl ${isDark ? 'text-white/80' : 'text-black/75'}`, "text-[24px]")} style={{
                  fontSize: "24px"
                }} data-id="element-947" data-mp="30qjtzdjoqup0">
                        How much?
                      </label>
                      {/* Floating stepper — matches Product page and
                       Services hub: big bold + and − pads, no container
                       background. Below full load: +1 yd. At/above:
                       snap by full load (2, 3, 4 loads). + at 4 loads
                       flips to amber 5+ warning link; − clears it.
                       Manipulates the existing quantity/numLoads pair
                       so all downstream quote math stays unchanged. */}
                      {(() => {
                  const light = isLightweight(material);
                  const fullLoad = light ? 15 : 12;
                  const showWarning = quantity === 'multi' && (parseInt(numLoads, 10) >= 5 || numLoads === '5+');
                  const currentYards = quantity === 'multi' ? (parseInt(numLoads, 10) || 2) * fullLoad : parseInt(quantity, 10) || fullLoad;
                  const loadCount = currentYards > fullLoad ? Math.ceil(currentYards / fullLoad) : 1;
                  const isFullLoad = currentYards === fullLoad;
                  const isMin = currentYards === 3;
                  const handleDec = () => {
                    triggerMinusFlash();
                    if (currentYards <= fullLoad) {
                      if (currentYards > 3) {
                        setQuantity(String(currentYards - 1));
                        setNumLoads('');
                        setCustomQty(null);
                      } else {
                        // At the 3 yd floor — flash the readout
                        // amber so the customer feels the limit
                        // instead of seeing the tap do nothing.
                        triggerMinFlash();
                      }
                    } else {
                      const loads = Math.round(currentYards / fullLoad);
                      if (loads <= 2) {
                        setQuantity(String(fullLoad));
                        setNumLoads('');
                      } else {
                        setQuantity('multi');
                        setNumLoads(String(loads - 1));
                      }
                    }
                    setCustomQty(null);
                  };
                  const handleInc = () => {
                    triggerPlusFlash();
                    if (currentYards < fullLoad) {
                      setQuantity(String(currentYards + 1));
                      setNumLoads('');
                    } else {
                      const loads = currentYards === fullLoad ? 1 : Math.round(currentYards / fullLoad);
                      // Cap at 8 loads. Past 4 loads the primary CTA
                      // flips to the amber "text us" button — but
                      // the stepper still works so the customer can
                      // show us the exact yardage they need.
                      if (loads >= 8) {
                        setCustomQty(null);
                        return;
                      }
                      setQuantity('multi');
                      setNumLoads(String(loads + 1));
                    }
                    setCustomQty(null);
                  };
                  return <div className="space-y-2" data-id="element-948" data-mp="2g2wvahk00glk">
                            <div className="flex items-stretch gap-4 sm:gap-6 pb-5" data-id="element-949" data-mp="2l0yf54p90jy3">
                              {/* Yardage readout — matched to the height and
                               value typography of the address and material boxes above. */}
                              <div className={`estimator-inset-rim ${isDark ? 'estimator-inset-rim--dark' : 'estimator-inset-rim--light'} relative flex-1 flex items-center px-6 sm:px-8 h-[68px] sm:h-[80px] rounded-[20px] border ${isDark ? 'bg-white/[0.07] border-white/[0.125] text-white backdrop-blur-md' : 'bg-white border-[#d6d3d1] text-stone-900'} text-xl sm:text-2xl font-medium tabular-nums leading-none`} data-id="element-950" data-mp="2u2fv8n2nolms">
                                {(() => {
                          // Digit color encodes state:
                          // - amber flash when user hits the
                          //   3 yd floor (replaces the old
                          //   "(min)" subtext)
                          // - subtle sage green when exactly
                          //   at one full load (replaces the
                          //   old "(full load)" subtext)
                          // - neutral otherwise
                          const tintClass = isMin ? isDark ? 'text-[#F6A93B]' : 'text-[#CB6E0A]' : isDark ? 'text-white' : 'text-stone-900';
                          // flashMin still triggers a brief
                          // tactile pulse when "−" is tapped at
                          // the floor, layered on top of the
                          // persistent amber by upping opacity.
                          void flashMin;
                          const commit = () => {
                            const parsed = parseInt(qtyDraft, 10);
                            if (isNaN(parsed) || parsed <= 0) {
                              setQtyDraft(String(currentYards));
                              return;
                            }
                            const max = fullLoad * 8;
                            const clamped = Math.min(max, Math.max(1, parsed));
                            // Honor the exact typed yardage. We
                            // used to snap to the nearest full
                            // load (e.g. type 25, get 30) which
                            // confused customers who entered a
                            // calculator-derived number. The
                            // loadCount derivation downstream
                            // (Math.ceil(yards / fullLoad)) takes
                            // care of multi-delivery sizing for
                            // the delivery fee.
                            setQuantity(String(clamped));
                            setNumLoads('');
                            setQtyDraft(String(clamped));
                            setCustomQty(null);
                          };
                          return <>
                                      <div className="flex items-baseline" data-id="element-951" data-mp="3l40938xyha4n">
                                        <input ref={qtyInputRef} type="text" inputMode="numeric" pattern="[0-9]*" value={qtyDraft} maxLength={2} onFocus={e => e.currentTarget.select()} onChange={e => {
                                const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                                setQtyDraft(v);
                              }} onBlur={commit} onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  (e.currentTarget as HTMLInputElement).blur();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  setQtyDraft(String(currentYards));
                                  (e.currentTarget as HTMLInputElement).blur();
                                }
                              }} aria-label="Quantity in yards" style={{
                                width: `${Math.max(1, qtyDraft.length)}ch`
                              }} className={`bg-transparent border-0 outline-none text-center text-xl sm:text-2xl font-medium tabular-nums p-0 m-0 focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-colors ${tintClass}`} data-id="element-952" data-mp="3nicltuo7o82" />
                                        <span className={`ml-2 text-xl font-medium transition-colors sm:text-2xl ${tintClass}`} data-id="element-953" data-mp="2l33ikdpy56xe">
                                          yards
                                        </span>
                                        <span className={`ml-2 text-xs font-medium align-baseline tracking-tight whitespace-nowrap sm:text-sm ${isDark ? 'text-white/70' : 'text-stone-600'}`} data-id="element-954" data-mp="2om2lkt6r0d72">
                                          (~{getEstimatedTons(material, currentYards)} tons)
                                        </span>
                                      </div>
                                    </>;
                        })()}
                              </div>
                              {/* −/+ controls — each sits inside a circular
                               pad that uses the SAME surface/border as the
                               yardage readout box on the left, so the row
                               reads as a matched set. */}
                              <div className="flex items-center justify-end gap-2.5 sm:gap-3 shrink-0" data-id="element-955" data-mp="3q28s3e3aw8kk">
                                <button type="button" onClick={handleDec} aria-label="Decrease quantity" style={{
                          WebkitTapHighlightColor: 'transparent'
                        }} className={`group flex items-center justify-center w-[64px] sm:w-[72px] self-stretch shrink-0 bg-transparent border-0 transition-colors duration-150 ${minusFlash ? isDark ? 'text-[#F6A93B]' : 'text-[#CB6E0A]' : isDark ? 'text-white' : 'text-stone-900'}`} data-id="element-956" data-mp="3v1czbz6nzw89">
                                  <Minus className="w-9 h-9 sm:w-11 sm:h-11 transition-transform duration-150 ease-out group-active:scale-125" strokeWidth={3} data-id="element-957" data-mp="3ad2bh7g4l9gl" />
                                </button>
                                <button type="button" onClick={handleInc} aria-label="Increase quantity" style={{
                          WebkitTapHighlightColor: 'transparent'
                        }} className={`group flex items-center justify-center w-[64px] sm:w-[72px] self-stretch shrink-0 bg-transparent border-0 transition-colors duration-150 ${plusFlash ? isDark ? 'text-[#22c95c]' : 'text-[#059F40]' : isDark ? 'text-white' : 'text-stone-900'}`} data-id="element-958" data-mp="2hpg9u4qgkewd">
                                  <Plus className="w-9 h-9 sm:w-11 sm:h-11 transition-transform duration-150 ease-out group-active:scale-125" strokeWidth={3} data-id="element-959" data-mp="eetc8r11bx46" />
                                </button>
                              </div>
                            </div>

                            {/* Live price window — updates in real time as
                             the customer adjusts address / material /
                             stepper. Shows the full picture: materials
                             with small-load surcharge applied, per-load
                             delivery from the zone lookup, and the
                             grand total. This replaces the old
                             post-submit estimate interstitial. */}
                            {productMap[material] && (() => {
                      const basePrice = productMap[material].pricePerYard;
                      // Tiered bulk discount — mirrors
                      // CartContext math so the live total
                      // matches what they see in the cart.
                      // 5% at 1 full load (light) / 2 full
                      // loads (heavy). 10% at 4 full loads.
                      const _light = isLightweight(material);
                      const _fullLoad = _light ? 15 : 12;
                      const bulkPct = currentYards >= _fullLoad * 4 ? 0.1 : currentYards >= (_light ? _fullLoad : _fullLoad * 2) ? 0.05 : 0;
                      // Split into full-load yards (charged at
                      // base) and partial-yards (charged at
                      // small-load surcharge). When both are
                      // non-zero we render two material lines
                      // so the customer sees exactly which
                      // yards trigger the higher rate.
                      const _fullLoadCount = Math.floor(currentYards / _fullLoad);
                      const _fullLoadYards = _fullLoadCount * _fullLoad;
                      const _partialYards = currentYards - _fullLoadYards;
                      const _partialSurcharge = _partialYards > 0 ? getSmallLoadSurcharge(_partialYards, material) : 1;
                      const fullLoadPerYd = Math.ceil(basePrice * (1 - bulkPct));
                      const partialPerYd = _partialYards > 0 ? Math.ceil(basePrice * _partialSurcharge * (1 - bulkPct)) : 0;
                      const fullLoadSubtotal = _fullLoadYards * fullLoadPerYd;
                      const partialSubtotal = _partialYards * partialPerYd;
                      const materialsSubtotal = fullLoadSubtotal + partialSubtotal;
                      const hasMixedBreakdown = _fullLoadCount > 0 && _partialYards > 0;
                      const isSmallLoad = _partialSurcharge > 1;
                      // Micro-load: a manually-entered 1–2 yd single
                      // load. Priced at the base per-yard rate (no
                      // surcharge) plus a flat $250 delivery fee that
                      // covers the truck run. Used to relabel the
                      // materials line and surface the flat-fee note.
                      const isMicroLoad = loadCount === 1 && currentYards > 0 && currentYards < 3;
                      const zoneFee = addressZone && addressZone.zone !== null ? addressZone.fee : null;
                      // Delivery total mirrors the Add-to-Truck
                      // button: each full load pays the zone fee, but
                      // a last/partial load under 3 yd swaps to the
                      // flat $250 micro-load fee.
                      const _lastLoadYards = currentYards - (loadCount - 1) * _fullLoad;
                      // Is the last (partial) load a micro-load
                      // (1–2 yd)? Those carry a flat $250 delivery
                      // fee instead of the zone rate, so they're
                      // billed — and displayed — separately from the
                      // standard zone-fee loads.
                      const _microLoadPresent = _lastLoadYards > 0 && _lastLoadYards < 3;
                      // Loads billed at the standard zone fee (every
                      // load except a trailing micro-load).
                      const _standardLoadCount = _microLoadPresent ? loadCount - 1 : loadCount;
                      const _standardDeliverySubtotal = zoneFee !== null ? _standardLoadCount * zoneFee : null;
                      const _microDeliveryFee = _microLoadPresent ? 250 : 0;
                      const deliveryTotal = zoneFee !== null ? (_standardDeliverySubtotal || 0) + _microDeliveryFee : null;
                      const grandTotal = deliveryTotal !== null ? materialsSubtotal + deliveryTotal : null;
                      // Past 4 full loads (≥ 5 loads) the
                      // standard discount tier no longer
                      // applies — these orders qualify for
                      // larger custom discounts negotiated
                      // by phone/text. Blur the breakdown
                      // so the customer doesn't anchor on
                      // the displayed number, and surface a
                      // clear "text us" handoff overlay.
                      const isLargeOrder = loadCount >= 5;
                      return <div className="relative mt-3" data-id="element-960" data-mp="1x0cfgc2zk6ec">
                                    <motion.div key={`${currentYards}-${material}-${zoneFee ?? 'na'}`} initial={{
                          opacity: 0,
                          y: -4
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} transition={{
                          duration: 0.2
                        }} aria-hidden={isLargeOrder} className={`pt-1 space-y-2 transition-all duration-300 ${isLargeOrder ? 'blur-[6px] opacity-50 pointer-events-none select-none' : ''}`} data-id="element-961" data-mp="1rywrab5ihjrq">
                                      {/* Materials section — splits
                                       into TWO lines when the order is a
                                       full load + partial mix, so the
                                       customer sees exactly which yards
                                       are at the full-load rate and which
                                       yards trigger the small-load
                                       surcharge. */}
                                      {hasMixedBreakdown ? <div data-id="element-962" data-mp="2s8eddgpsbanz">
                                          <div className={`text-xs uppercase tracking-wider font-bold mb-1 ${isDark ? 'text-white' : 'text-stone-500'}`} data-id="element-963" data-mp="2s5js5myy5lti">
                                            Materials
                                          </div>
                                          {/* Full-load line */}
                                          <div className="flex items-baseline justify-between gap-3" data-id="element-964" data-mp="29h4zywcw81r4">
                                            <div className={`min-w-0 text-xs flex items-center flex-wrap gap-x-2 ${isDark ? 'text-white' : 'text-stone-600'}`} data-id="element-965" data-mp="2eg850gsvi7z3">
                                              <span data-id="element-966" data-mp="1mdiz8gom1gh7">
                                                ${fullLoadPerYd}/yd ×{' '}
                                                {_fullLoadYards} yd
                                              </span>
                                              {bulkPct > 0 ? <span className="text-[#059F40] font-semibold" data-id="element-967" data-mp="21bbf59y2k6vx">
                                                  {Math.round(bulkPct * 100)}%
                                                  bulk discount
                                                </span> : null}
                                            </div>
                                            <div className={`text-sm font-bold tabular-nums shrink-0 ${isDark ? 'text-white' : 'text-stone-900'}`} data-id="element-968" data-mp="1dje3biumdeut">
                                              $
                                              {fullLoadSubtotal.toLocaleString()}
                                            </div>
                                          </div>
                                          {/* Partial-load line. Only tag
                                           it "Small-load rate" when the
                                           partial actually triggers a
                                           surcharge (below threshold). A
                                           partial at/above threshold —
                                           e.g. an 11 yd remainder on a
                                           35 yd order — runs at the
                                           full-load/bulk rate. */}
                                          <div className="flex items-baseline justify-between gap-3 mt-0.5" data-id="element-969" data-mp="2be6yi85obuz8">
                                            <div className={`min-w-0 text-xs flex items-center flex-wrap gap-x-2 ${isDark ? 'text-white' : 'text-stone-600'}`} data-id="element-970" data-mp="26gybnu9kz2rn">
                                              <span data-id="element-971" data-mp="1whpkf963o28z">
                                                ${partialPerYd}/yd ×{' '}
                                                {_partialYards} yd
                                              </span>
                                              {_partialSurcharge > 1 ? <button type="button" onClick={() => setShowSmallLoadInfo(value => !value)} className={`inline-flex min-h-7 items-center gap-1 rounded-md px-1.5 font-semibold underline-offset-2 transition-colors hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${isDark ? 'text-[#F6A93B] focus-visible:ring-[#F6A93B] focus-visible:ring-offset-[#111111]' : 'text-[#CB6E0A] focus-visible:ring-[#CB6E0A] focus-visible:ring-offset-white'}`} aria-expanded={showSmallLoadInfo} aria-controls="small-load-rate-explainer" data-id="element-972" data-mp="1rr3peju69zsp">
                                                  Small-load rate
                                                  <Info className="h-3.5 w-3.5" aria-hidden="true" data-id="element-973" data-mp="2oixms3optakn" />
                                                </button> : bulkPct > 0 ? <span className="text-[#059F40] font-semibold" data-id="element-974" data-mp="2taz2jzl3djff">
                                                  {Math.round(bulkPct * 100)}%
                                                  bulk discount
                                                </span> : null}
                                            </div>
                                            <div className={`text-sm font-bold tabular-nums shrink-0 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-975" data-mp="mlx276i8ejy1">
                                              $
                                              {partialSubtotal.toLocaleString()}
                                            </div>
                                          </div>
                                        </div> : isSmallLoad ? <div className={`text-xs ${isDark ? 'text-white/80' : 'text-stone-600'}`} data-id="element-976" data-mp="3vfczbwly6hf">
                                          <button type="button" onClick={() => setShowSmallLoadInfo(value => !value)} className={`inline-flex min-h-7 items-center gap-1 rounded-md px-1.5 font-semibold underline-offset-2 transition-colors hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${isDark ? 'text-[#F6A93B] focus-visible:ring-[#F6A93B] focus-visible:ring-offset-[#111111]' : 'text-[#CB6E0A] focus-visible:ring-[#CB6E0A] focus-visible:ring-offset-white'}`} aria-expanded={showSmallLoadInfo} aria-controls="small-load-rate-explainer" data-id="element-977" data-mp="1k95vhnoh4xal">
                                            Small-load rate
                                            <Info className="h-3.5 w-3.5" aria-hidden="true" data-id="element-978" data-mp="7rjsbm2y1tuc" />
                                          </button>
                                        </div> : null}
                                      {/* Small-load explainer — appears on
                                       tap of the amber chip above. Short,
                                       plain language; not a full essay. */}
                                      {isSmallLoad && showSmallLoadInfo && <motion.div id="small-load-rate-explainer" initial={{
                            opacity: 0,
                            height: 0
                          }} animate={{
                            opacity: 1,
                            height: 'auto'
                          }} exit={{
                            opacity: 0,
                            height: 0
                          }} className="overflow-hidden" data-id="element-979" data-mp="vhnsfjgxbbgl">
                                          <div className={`text-[11px] rounded-lg px-3 py-2 leading-relaxed border ${isDark ? 'text-[#F6A93B]/90 bg-[#F6A93B]/10 border-[#F6A93B]/30' : 'text-[#CB6E0A] bg-[#CB6E0A]/5 border-[#CB6E0A]/30'}`} data-id="element-980" data-mp="ro5ub8jzuy5f">
                                            Orders under a full load share the
                                            same truck run, so the per-yard rate
                                            is slightly higher. It scales down
                                            to the full-load rate at 10+ yd.
                                          </div>
                                        </motion.div>}
                                      {/* Delivery row — one line: label +
                                       zone inline, fee on the right. Zone
                                       label uses the zone's map color so
                                       the breakdown matches the pill
                                       above. */}
                                      {zoneFee !== null && deliveryTotal !== null ? <div className="space-y-1.5" data-id="element-981" data-mp="3dt7q49nwn4ke">
                                          {/* Standard zone-fee loads — combined
                                           into ONE row when they all cost the
                                           same (e.g. "Zone 1 × 2 loads"). Only
                                           shown when at least one full/standard
                                           load exists. */}
                                          {_standardLoadCount > 0 && _standardDeliverySubtotal !== null && <div className="flex items-baseline justify-between gap-3" data-id="element-982" data-mp="171q7g2ceyg1t">
                                                <div className={`min-w-0 truncate text-[20px] font-[500] ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-983" data-mp="2rllj7f0nxeyq">
                                                  <span data-id="element-984" data-mp="31almvbq40yj9">Delivery</span>
                                                  <span className="mx-1.5" data-id="element-985" data-mp="31cy8byv2tr12">·</span>
                                                  <span className="font-normal text-[12px]" data-id="element-986" data-mp="2wiu2jixjrp1k">Zone {addressZone?.zone}</span>
                                                  <span className="text-[14px] font-normal" data-id="element-987" data-mp="3adee5ezala93"> × {_standardLoadCount} {_standardLoadCount === 1 ? 'load' : 'loads'}</span>
                                                </div>
                                                <div className={cn(`shrink-0 text-2xl font-[500] tabular-nums ${isDark ? 'text-white/90' : 'text-black/90'}`, "text-[20px]")} data-id="element-988" data-mp="3fawewi35oj60">
                                                  {_standardDeliverySubtotal === 0 ? 'Free' : `${_standardDeliverySubtotal.toLocaleString()}`}
                                                </div>
                                              </div>}
                                          {/* Micro-load delivery — its own row
                                           with a short description, since it's a
                                           different (flat $250) price from the
                                           zone-fee loads above. */}
                                          {_microLoadPresent && <div className="flex items-start justify-between gap-3" data-id="element-989" data-mp="g6esvs47usma">
                                              <div className={`text-xs min-w-0 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-990" data-mp="l3x3dmxvtfwe">
                                                <span className={`uppercase tracking-wider font-bold ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-991" data-mp="v1us0bjnlepj">
                                                  Micro-load delivery
                                                </span>
                                                <span className={`block text-[11px] mt-0.5 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-992" data-mp="26j4aaur1q0p9">
                                                  {_lastLoadYards} yd load ·
                                                  flat fee (under 3 yd)
                                                </span>
                                              </div>
                                              <div className={`text-base font-bold tabular-nums shrink-0 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-993" data-mp="3c9w6g5l9kvy3">
                                                $
                                                {_microDeliveryFee.toLocaleString()}
                                              </div>
                                            </div>}
                                        </div> : <div className={`text-xs italic ${isDark ? 'text-white/50' : 'text-stone-400'}`} data-id="element-994" data-mp="2td4lzstzkasg">
                                          Enter your address to include delivery
                                        </div>}
                                      {/* Micro-load (1–2 yd) flat-fee note —
                                       explains why delivery jumps from the
                                       zone rate to a flat $250, and nudges a
                                       round-up to drop it. */}
                                      {isMicroLoad && zoneFee !== null && <div className={`flex items-start gap-1.5 text-[11px] rounded-lg px-3 py-2 leading-relaxed border ${isDark ? 'text-[#F6A93B]/90 bg-[#F6A93B]/10 border-[#F6A93B]/30' : 'text-[#CB6E0A] bg-[#CB6E0A]/5 border-[#CB6E0A]/30'}`} data-id="element-995" data-mp="1mlvp8s3sohbd">
                                          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-996" data-mp="17w69nruygark" />
                                          <span data-id="element-997" data-mp="13ubb2v4esbhh">
                                            Loads under 3 yd use a flat $250
                                            micro-load delivery fee. Round up to{' '}
                                            <span className={`font-bold ${isDark ? 'text-white' : 'text-stone-900'}`} data-id="element-998" data-mp="2f9rkrthmo36k">
                                              3+ yd
                                            </span>{' '}
                                            to switch to the Zone{' '}
                                            {addressZone?.zone} rate (${zoneFee}
                                            ).
                                          </span>
                                        </div>}
                                      {/* Delivery and total remain one compact
                                       estimate. A quiet divider now introduces
                                       the payment reassurance below them. */}
                                      {grandTotal !== null && <>
                                          <div className="flex items-baseline justify-between gap-3" data-id="element-999" data-mp="30r7jufkxivk7">
                                            <div className={cn(`text-sm font-bold ${isDark ? 'text-white/90' : 'text-black/90'}`, 'text-[20px] font-[500]')} data-id="element-1000" data-mp="3ohbcxt0hrcwy">
                                              Estimated total
                                            </div>
                                            <div className={cn(`text-2xl font-[500] tabular-nums ${isDark ? 'text-white/90' : 'text-black/90'}`, "text-[20px]")} data-id="element-1001" data-mp="1zt1tbcg22g0">
                                              ${grandTotal.toLocaleString()}
                                            </div>
                                          </div>
                                          <div className={`mt-3 border-t ${isDark ? 'border-white/10' : 'border-stone-300'}`} data-id="element-1002" data-mp="3k7iir7wc3g4g" />
                                          <div className={`mt-3 flex justify-end text-xs font-medium ${isDark ? 'text-white/65' : 'text-stone-600'}`} data-id="element-1003" data-mp="3fdeb0mf8v42b">
                                            No credit card needed · Pay on delivery
                                          </div>
                                        </>}
                                    </motion.div>
                                    {/* Large-order overlay — sits on top
                                     of the blurred breakdown card and
                                     tells the customer to text us for a
                                     custom quote. The Add-to-Truck CTA
                                     below morphs into "Large order —
                                     text us →" at the same threshold. */}
                                    {isLargeOrder && <motion.div initial={{
                          opacity: 0,
                          y: 4
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} transition={{
                          duration: 0.25
                        }} className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 pointer-events-none" data-id="element-1004" data-mp="t82yypg3c4uu">
                                        <p className="text-base font-extrabold text-[#DD4132] tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" data-id="element-1005" data-mp="ah39ln4oot11">
                                          Custom pricing for {loadCount}+ loads
                                        </p>
                                        <p className="text-xs text-white/90 mt-1 leading-snug drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]" data-id="element-1006" data-mp="3mlxs8oykps27">
                                          Volume discounts apply — text us for
                                          your exact quote.
                                        </p>
                                      </motion.div>}
                                  </div>;
                    })()}

                            {/* Large-order handoff — the primary CTA
                             below morphs into amber "Text us" when +
                             has pushed past 4 loads. */}

                            {loadCount > 1 && !showWarning && <div className="flex items-start gap-2 p-3 bg-[#059F40]/10 border border-[#059F40]/20 rounded-none" data-id="element-1007" data-mp="2pu2k3g2kzrix">
                                <Info className="w-4 h-4 text-[#059F40] mt-0.5 shrink-0" data-id="element-1008" data-mp="2kwareju441do" />
                                <p className={`text-xs font-semibold ${isDark ? 'text-stone-100' : 'text-stone-900'}`} data-id="element-1009" data-mp="10e9r7uv8vi86">
                                  Multi-load orders require separate deliveries.
                                  You will be charged a delivery fee per load.
                                </p>
                              </div>}
                          </div>;
                })()}
                    </motion.div>}
                </AnimatePresence>

                {/* Submit button — only when address + material + quantity are ready */}
                <AnimatePresence data-id="element-1010" data-mp="llzkp0nnag5e">
                  {showMaterialRow && material && quantity && (quantity !== 'multi' || numLoads) && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-1011" data-mp="cne255i6b14m">
                        {/* Same-day countdown — only renders Mon–Fri
                         before the 12 PM cutoff (returns null otherwise).
                         Sits directly above the Add-to-Truck CTA so the
                         "Get it today" nudge is visible right where the
                         customer commits. */}
                        <div className="mb-4 flex justify-center" data-id="element-1012" data-mp="2obixeq7auqg9">
                          <SameDayBadge variant="full" data-id="element-1013" data-mp="2ocu0ensx7u6b" />
                        </div>
                        {quantity === 'multi' && parseInt(numLoads, 10) >= 5 ? <button type="button" onClick={() => {
                  const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                  if (!isMobile) {
                    setIsQuoteModalOpen(true);
                    return;
                  }
                  const light = isLightweight(material);
                  const fullLoad = light ? 15 : 12;
                  const loads = parseInt(numLoads, 10) || 5;
                  const yds = loads * fullLoad;
                  const parts = [`Hi 206Gravel — I need a custom quote for ${loads} loads of ${material || 'material'} (${yds} yd).`];
                  if (address) parts.push(`Delivering to: ${address}.`);
                  parts.push('Thanks!');
                  const body = parts.join(' ');
                  window.location.href = `sms:+12066193742?&body=${encodeURIComponent(body)}`;
                }} className="w-full px-6 py-5 sm:py-6 min-h-[92px] sm:min-h-[106px] rounded-2xl font-bold text-lg transition-colors flex items-center justify-center bg-[#DD4132]/15 dark:bg-[#DD4132]/15 hover:bg-[#DD4132]/25 dark:hover:bg-[#DD4132]/25 text-[#DD4132] dark:text-[#DD4132] border-2 border-[#DD4132]/40 hover:border-[#DD4132]/60" aria-live="polite" data-id="element-1014" data-mp="2ekej7spg79mh">
                            <MessageSquare className="w-5 h-5 mr-2" data-id="element-1015" data-mp="l1nwyrr4gegg" />
                            Large order — text us →
                          </button> : (() => {
                  // Compute live grand total at the button so
                  // the label can show "Add to Truck — $XXX".
                  // Mirrors the math in the live price window
                  // above; the two stay in sync because both
                  // use getSmallLoadSurcharge + addressZone.fee
                  // × loadCount.
                  const productInfo = productMap[material];
                  const light = isLightweight(material);
                  const fullLoad = light ? 15 : 12;
                  const yards = quantity === 'multi' ? (parseInt(numLoads, 10) || 2) * fullLoad : parseInt(quantity, 10) || fullLoad;
                  const loads = yards > fullLoad ? Math.round(yards / fullLoad) : 1;
                  const basePrice = productInfo?.pricePerYard ?? 0;
                  const surcharge = getSmallLoadSurcharge(yards, material);
                  const effectivePerYd = Math.ceil(basePrice * surcharge);
                  const materialsSubtotal = effectivePerYd * yards;
                  const zoneReady = addressZone && addressZone.zone !== null;
                  const deliveryTotal = zoneReady ? (loads - 1) * addressZone!.fee + (yards - (loads - 1) * fullLoad < 3 ? 250 : addressZone!.fee) : 0;
                  const grandTotal = materialsSubtotal + deliveryTotal;
                  const handleAddToTruckDirect = () => {
                    if (!productInfo || !zoneReady) return;
                    // Persist delivery info so the cart drawer
                    // shows the right zone/address from the
                    // moment it opens. setDeliveryInfo is a
                    // POSITIONAL setter: (address, fee, zone)
                    // — passing an object here silently stuffs
                    // the whole object into the address slot
                    // and later .trim() calls on it explode.
                    setDeliveryInfo(address, addressZone!.fee, addressZone!.zone);
                    const items = buildDeliveryLineItems(yards, material);
                    items.forEach(it => {
                      addToCart({
                        category: productInfo.categoryName,
                        product: it.product,
                        quantity: it.quantity,
                        pricePerYard: productInfo.pricePerYard,
                        source: 'estimator',
                        requestedQuantity: it.requestedQuantity
                      });
                    });
                    openCart();
                  };
                  // ── Easter-egg slider ──────────────────────────
                  // Turn the CTA into a hidden yardage slider. A
                  // horizontal drag maps the pointer's x-position
                  // across the button to a yardage in [3, fullLoad]
                  // and writes it straight into the quantity state
                  // (single-load range only). Movement past a small
                  // threshold flags the gesture as a slide so the
                  // click handler swallows the tap instead of adding
                  // to the cart.
                  const applyTruckSlide = (clientX: number, el: HTMLElement) => {
                    const rect = el.getBoundingClientRect();
                    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
                    const yds = Math.round(3 + frac * (fullLoad - 3));
                    setQuantity(String(yds));
                    setNumLoads('');
                    setCustomQty(null);
                  };
                  const handleTruckPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
                    truckDragRef.current = {
                      active: true,
                      moved: false,
                      startX: e.clientX,
                      pointerId: e.pointerId
                    };
                    try {
                      e.currentTarget.setPointerCapture(e.pointerId);
                    } catch {}
                  };
                  const handleTruckPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
                    const d = truckDragRef.current;
                    if (!d.active) return;
                    if (Math.abs(e.clientX - d.startX) > 6) {
                      if (!d.moved) setTruckSliding(true);
                      d.moved = true;
                    }
                    if (d.moved) applyTruckSlide(e.clientX, e.currentTarget);
                  };
                  const handleTruckPointerEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
                    const d = truckDragRef.current;
                    d.active = false;
                    try {
                      e.currentTarget.releasePointerCapture(d.pointerId);
                    } catch {}
                    // Let the fill settle back to its eased animation
                    // once the drag ends.
                    setTruckSliding(false);
                  };
                  // "Load bar" effect — the bright green
                  // fill animates from full (at one truckload)
                  // down toward ~25% (at the 3 yd minimum),
                  // revealing a darker green underneath. Above
                  // a full load (multi-delivery) it stays at
                  // 100%. Visualizes the truck filling up as
                  // the customer increases yardage.
                  const loadPct = Math.min(100, Math.max(0, yards / fullLoad * 100));
                  // On a fresh material selection the bright fill
                  // shouldn't snap to the full-load width — it should
                  // appear already mostly full (at the 10yd mark) and
                  // then ease the remaining 10yd → full-load sliver in,
                  // decelerating so the final ~0.5yd creeps in slowly.
                  const revealStartPct = Math.min(loadPct, 10 / fullLoad * 100);
                  return <>
                      <button type="button" onClick={e => {
                      // Swallow the tap if this was actually a
                      // slide (Easter-egg yardage drag). A plain
                      // tap falls through to add-to-cart.
                      if (truckDragRef.current.moved) {
                        truckDragRef.current.moved = false;
                        e.preventDefault();
                        return;
                      }
                      handleAddToTruckDirect();
                    }} onPointerDown={handleTruckPointerDown} onPointerMove={handleTruckPointerMove} onPointerUp={handleTruckPointerEnd} onPointerCancel={handleTruckPointerEnd} disabled={!address || !zoneReady} style={{
                      touchAction: 'pan-y'
                    }} className="relative flex min-h-[83px] w-full select-none items-center justify-center overflow-hidden rounded-none bg-[#006239] px-6 py-4 text-lg font-bold text-white shadow-[0_0_20px_rgba(5,159,64,0.3)] transition-colors disabled:opacity-70 sm:min-h-[95px] sm:py-5" data-id="element-1016" data-mp="35xh6m7nc3ydp">
                                {/* Two-layer load bar: the darker #006239 base
                                 is revealed as the #04A05C fill recedes,
                                 visualizing the truck emptying in both themes. */}
                                <motion.span key={loadBarRevealKey} aria-hidden="true" className="absolute inset-y-0 left-0 z-0 bg-[#04A05C]"
                      // Keying on loadBarRevealKey re-mounts this on
                      // each material pick so it animates FROM the
                      // 10yd mark up to the default full-load width.
                      // +/- quantity changes (no key change) animate
                      // from the current width instead.
                      initial={{
                        width: `${revealStartPct}%`
                      }} animate={{
                        width: `${loadPct}%`
                      }} transition={truckSliding ? {
                        // While the Easter-egg slide is
                        // active, the fill tracks the finger
                        // instantly instead of easing.
                        duration: 0
                      } : {
                        // Decelerating ease-out so the fill
                        // rushes in then slows hard at the end
                        // — the last sliver (final ~0.5yd)
                        // creeps in slowly.
                        duration: 3.2,
                        ease: [0.16, 1, 0.3, 1]
                      }} data-id="element-1017" data-mp="1tnrttpe9eta6" />
                                <span className="relative z-10 flex items-center justify-center" data-id="element-1018" data-mp="2brrusx5megv9">
                                  {zoneReady ? <>
                                      Add to Truck — $
                                      {grandTotal.toLocaleString()}
                                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1019" data-mp="hfv7nge7nx60" />
                                    </> : <>
                                      Enter your address to continue
                                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1020" data-mp="ev5npxjibse2" />
                                    </>}
                                </span>
                              </button>
                    </>;
                })()}
                        {/* Secondary actions — plain text links (no pill
                         chrome) so the primary Add-to-Truck CTA stays the
                         clear focus. Keeps the phone-in and product-detail
                         paths available without competing button visuals. */}
                        {productMap[material] && <div className="mt-3 flex items-center justify-center gap-5 pt-2.5 text-[17px]" data-id="element-1021" data-mp="nsprpe8r82sd">
                            <a href="tel:206-619-3742" className={`inline-flex items-center gap-1.5 font-semibold transition-colors ${isDark ? 'text-white/70 hover:text-white' : 'text-stone-700 hover:text-stone-900'}`} data-id="element-1022" data-mp="jrdxevexqtdu">
                              <Phone className={`w-5 h-5 ${isDark ? 'text-white/75' : 'text-[#059F40]'}`} data-id="element-1023" data-mp="34h3ab2w2w53w" />
                              Call to Order
                            </a>
                            <span className={isDark ? 'text-white/20' : 'text-stone-300'} aria-hidden="true" data-id="element-1024" data-mp="1cieodneo6u8k">|</span>
                            <Link to={`/shop/${productMap[material].slug}?qty=${encodeURIComponent(quantity === 'multi' ? String((parseInt(numLoads, 10) || 2) * (isLightweight(material) ? 15 : 12)) : quantity)}`} className={`inline-flex items-center gap-1.5 font-semibold transition-colors ${isDark ? 'text-white/70 hover:text-white' : 'text-stone-700 hover:text-stone-900'}`} data-id="element-1025" data-mp="yiwxn2htd6wk">
                              <FileText className="w-5 h-5 text-[#059F40]" data-id="element-1026" data-mp="1hf505yjfz3o6" />
                              View Material
                            </Link>
                          </div>}
                      </motion.div>}
                </AnimatePresence>
              </form>

              <div className="mt-5 flex flex-row flex-nowrap items-center justify-center gap-2 sm:gap-4 text-[11px] sm:text-xs text-stone-700" data-id="element-1027" data-mp="2evkau1bm1sqa">
                <div className="flex items-center whitespace-nowrap text-[#888888]" data-id="element-1028" data-mp="2o8b2kvjhu1ka">
                  <Lock className="w-3 h-3 mr-1.5 shrink-0 opacity-70" data-id="element-1029" data-mp="2k80v1eg1z33b" />
                  Your information is secure
                </div>
                <span className={isDark ? 'text-white/20' : 'text-stone-400'} aria-hidden="true" data-id="element-1030" data-mp="2t0ud1jmvwgkv">
                  |
                </span>
                {(() => {
              // Compute current Pacific time + day. Dispatch hours:
              // Mon–Fri 5am–6pm, Sat 6am–3pm, Sun closed.
              const nowPT = new Date(new Date().toLocaleString('en-US', {
                timeZone: 'America/Los_Angeles'
              }));
              const day = nowPT.getDay(); // 0=Sun, 6=Sat
              const hour = nowPT.getHours();
              let isActive = false;
              let label = '';
              if (day === 0) {
                // Sunday — closed all day
                label = 'Dispatch Closed Sundays';
              } else if (day === 6) {
                isActive = hour >= 6 && hour < 15;
                label = isActive ? 'Dispatch Active' : 'Dispatch Opens 6 AM';
              } else {
                isActive = hour >= 5 && hour < 18;
                label = isActive ? 'Dispatch Active' : day === 5 && hour >= 18 ? 'Dispatch Opens Sat 6 AM' : 'Dispatch Opens 5 AM';
              }
              return <div className={cn('flex items-center gap-1.5 whitespace-nowrap font-medium', isActive ? 'text-[#00B774]' : 'text-[#888888]')} data-id="element-1031" data-mp="1xbtpeavl3zev">
                      <span className="relative flex h-2 w-2 shrink-0" data-id="element-1032" data-mp="1tai2id43hsad">
                        {isActive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#059F40] opacity-75" data-id="element-1033" data-mp="1ohfai9eyuqwa"></span>}
                        <span className={`relative inline-flex rounded-full h-2 w-2 ${isActive ? 'bg-[#059F40]' : 'bg-[#888888]'}`} data-id="element-1034" data-mp="2gzaslsy1d5x2"></span>
                      </span>
                      {label}
                    </div>;
            })()}
              </div>

              {/* Social proof closes the estimator. Sitting here rather than
                  above the form, it doubles as breathing room between the hero
                  and the materials section below. */}
              <div className="mt-8 flex items-center justify-center gap-2.5" data-id="element-1035" data-mp="2tglka4w58zwj">
                <div className="flex" data-id="element-1036" data-mp="2xk9u6u8907ky">
                  {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${isDark ? 'text-white/85 fill-white/85' : 'text-stone-700 fill-stone-700'}`} data-id="element-1037" data-mp="32j37omnjbyl4" data-mpi={i} />)}
                </div>
                <span className={`text-sm font-medium tabular-nums ${isDark ? 'text-white/85' : 'text-stone-900'}`} data-id="element-1038" data-mp="37ftk9004sc1s">
                  {liveDeliveries.toLocaleString()}+ {t('hero.deliveriesWord')}
                </span>
              </div>

              {/* Quote Result — DEPRECATED. Live price window + Add to
               Truck CTA above replace this post-submit interstitial.
               Kept as dead code (false &&) so the supporting state /
               refs / handlers stay valid and we have a quick rollback
               path if needed. */}
              {false && quoteResult && <motion.div ref={quoteResultRef} initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-5 pt-5 border-t border-stone-300/40 dark:border-white/10" data-id="element-1039" data-mp="1m5vykfknx1mg">
                  <p className="text-xs text-stone-700 dark:text-white/80 font-bold uppercase tracking-wider mb-3" data-id="element-1040" data-mp="29t44mzpx0epj">
                    Price Breakdown
                  </p>
                  <p className="text-sm text-stone-800 dark:text-white/90 font-medium mb-4" data-id="element-1041" data-mp="2iulew1gn5m6b">
                    {quoteResult.quantity} yards of{' '}
                    <span className="font-bold" data-id="element-1042" data-mp="2dws9han5ww73">{quoteResult.material}</span>
                    {' → '}
                    {quoteResult.address}
                  </p>

                  {/* Line items */}
                  <div className="space-y-2 mb-4" data-id="element-1043" data-mp="jj0vy01pps6d">
                    {/* Per-delivery breakdown for multi-load orders —
                     mirrors the Services hub and Product page. Each
                     delivery gets its own yards + dollar row so the
                     "rate × loads = total" math is fully transparent.
                     Single-load orders fall back to the compact
                     "Material — $X/yd × Y yds" row. */}
                    {quoteResult.deliveries && quoteResult.deliveries > 1 ? (() => {
                const totalYards = parseInt(quoteResult.quantity) || 0;
                const count = quoteResult.deliveries;
                const fullLoad = Math.floor(totalYards / count);
                const remainder = totalYards - fullLoad * count;
                // Distribute remainder across leading loads so
                // we don't end up with a stray 0-yard "last
                // load" — keeps the split intuitive when
                // quantity doesn't divide evenly.
                const perDelivery: number[] = [];
                for (let i = 0; i < count; i++) {
                  perDelivery.push(fullLoad + (i < remainder ? 1 : 0));
                }
                // If the calculator flagged a min-load
                // adjustment, the last load is BILLED higher
                // than its actual yardage. Surface that on
                // the last row.
                const lastActual = quoteResult.minLoadAdjustment?.lastLoadActual;
                return <>
                            {perDelivery.map((loadYards, idx) => {
                    const isLast = idx === perDelivery.length - 1;
                    const showCharged = isLast && typeof lastActual === 'number' && lastActual < loadYards;
                    const dollars = quoteResult.effectivePricePerYardLow * loadYards;
                    return <div key={idx} className="flex justify-between text-sm" data-id="element-1044" data-mp="2ag0ix1had9wx" data-mpi={idx}>
                                  <span className="text-stone-700 dark:text-white/70" data-id="element-1045" data-mp="26dx1zxbuld49">
                                    Delivery {idx + 1}: {loadYards} yds
                                    {showCharged && ` (charged as ${loadYards})`}
                                  </span>
                                  <span className="text-stone-900 dark:text-white font-semibold" data-id="element-1046" data-mp="ux2j3jnge8t2">
                                    ${dollars.toLocaleString()}
                                  </span>
                                </div>;
                  })}
                            <p className="text-xs text-brand-orange" data-id="element-1047" data-mp="29ylw0ulqyru">
                              + additional delivery fee for{' '}
                              {perDelivery.length - 1} extra{' '}
                              {perDelivery.length > 2 ? 'loads' : 'load'}
                            </p>
                          </>;
              })() : <div className="flex justify-between text-sm" data-id="element-1048" data-mp="3fn1mrgxgtvpv">
                        <span className="text-stone-700 dark:text-white/70" data-id="element-1049" data-mp="3fmrypyvzdymt">
                          Material — ${quoteResult.effectivePricePerYardLow}
                          /yd × {quoteResult.quantity} yds
                        </span>
                        <span className="text-stone-900 dark:text-white font-semibold" data-id="element-1050" data-mp="38kdaqhmona35">
                          $
                          {quoteResult.effectivePricePerYardLow * parseInt(quoteResult.quantity)}
                        </span>
                      </div>}
                    {quoteResult.minLoadAdjustment && <div className="-mt-1 ml-0.5" data-id="element-1051" data-mp="2tm1do9vedzbg">
                        <button type="button" onClick={() => setShowMinLoadInfo(v => !v)} className="inline-flex items-center gap-1.5 text-xs text-amber-500 dark:text-amber-400 font-medium hover:underline" aria-expanded={showMinLoadInfo} data-id="element-1052" data-mp="2tonku8edmct5">
                          <Info className="w-3.5 h-3.5" data-id="element-1053" data-mp="2yl4l20yuohml" />
                          You asked for{' '}
                          {quoteResult.minLoadAdjustment.requested} yds — billed
                          for {quoteResult.minLoadAdjustment.billable} yds. Why?
                        </button>
                        <AnimatePresence data-id="element-1054" data-mp="1vsjxyhn5ifdz">
                          {showMinLoadInfo && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-1055" data-mp="1vv66ajhv8yx9">
                              <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed space-y-1.5" data-id="element-1056" data-mp="2jimjeewk8n3m">
                                <p data-id="element-1057" data-mp="25peu4tvyc3p3">
                                  <span className="font-bold" data-id="element-1058" data-mp="2apj0vs725k24">
                                    3-yard order minimum.
                                  </span>{' '}
                                  You asked for{' '}
                                  {quoteResult.minLoadAdjustment.requested} yd —
                                  billed as 3 yd.
                                </p>
                                <p data-id="element-1059" data-mp="ydxb9pb85zmm">
                                  Trucks have fixed dispatch costs, so any
                                  single delivery is billed at 3 yds even if you
                                  only need a couple. Round up to fill the truck
                                  and you'll get more material at no extra cost.
                                </p>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>}
                    <SmallLoadBreakdown quantity={parseInt(quoteResult.quantity) || 0} productName={quoteResult.material} pricePerYard={quoteResult.basePricePerYardLow} className="-mt-1 ml-0.5" data-id="element-1060" data-mp="1c40oxrw1gl0g" />
                    {/* Green "X% bulk discount applied" line removed to
                     match Product and Services — the discount is
                     reflected in the per-delivery rows above and the
                     Estimated Total below. */}
                    <div className="flex justify-between text-sm" data-id="element-1061" data-mp="2cd8k97ng00py">
                      <span className="text-stone-700 dark:text-white/70" data-id="element-1062" data-mp="2cdic70s1shya">
                        Delivery —{' '}
                        {addressUnverified ? 'Confirmed by dispatch' : quoteResult.deliveryZone !== null ? `Zone ${quoteResult.deliveryZone}` : 'Custom Quote'}
                        {quoteResult.deliveries && quoteResult.deliveries > 1 ? ` (×${quoteResult.deliveries})` : ''}
                      </span>
                      <span className={`font-semibold ${addressUnverified ? 'text-amber-400' : 'text-stone-900 dark:text-white'}`} data-id="element-1063" data-mp="2w32k890b9m59">
                        {addressUnverified ? 'TBD' : quoteResult.deliveryZone !== null ? `${quoteResult.deliveryFee}` : 'TBD'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-stone-300/40 dark:border-white/10" data-id="element-1064" data-mp="1anwnao0rvn2o">
                      <span className="text-stone-900 dark:text-white font-bold text-sm" data-id="element-1065" data-mp="uhy2lagjw4e3">
                        Estimated Total
                      </span>
                      <span className="text-2xl font-heading font-extrabold text-stone-900 dark:text-white" data-id="element-1066" data-mp="zdwx7fsh2imy">
                        ${quoteResult.priceLow}
                      </span>
                    </div>
                  </div>

                  <div className="mb-4 flex justify-center" data-id="element-1067" data-mp="1d8fnwtz1zxpf">
                    <SameDayBadge variant="full" data-id="element-1068" data-mp="1da0hohdjncm8" />
                  </div>

                  <div className="flex flex-col gap-3" data-id="element-1069" data-mp="1i4wdztptcm55">
                    <button type="button" onClick={handleAddToTruckFromQuote} className="w-full bg-[#059F40] hover:bg-[#059F40] text-white px-6 py-[18px] rounded-lg font-bold text-lg transition-colors flex items-center justify-center shadow-[0_0_20px_rgba(5,159,64,0.25)]" data-id="element-1070" data-mp="1n5kdz4w0sdbi">
                      Add to Truck & Check Out
                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1071" data-mp="3tx334p1xh5fm" />
                    </button>
                    <div className={`grid ${quoteResult.materialSlug ? 'grid-cols-2' : 'grid-cols-1'} gap-2.5`} data-id="element-1072" data-mp="2b277ndp6jpbl">
                      <a href="tel:206-619-3742" className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-stone-300 dark:border-white/15 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 hover:border-[#059F40]/50 dark:hover:border-[#059F40]/50 text-stone-800 dark:text-white text-sm font-semibold transition-all" data-id="element-1073" data-mp="269nm9uk93pw8">
                        <Phone className="w-4 h-4 text-[#059F40]" data-id="element-1074" data-mp="2pypnxdc50zhk" />
                        Call to Order
                      </a>
                      {quoteResult.materialSlug && <Link to={`/shop/${quoteResult.materialSlug}?qty=${encodeURIComponent(
                // Pass the user's REQUESTED yardage (e.g. 14), not
                // the billable value (17 after 5yd-min adjustment).
                // The Product page re-applies the split via
                // buildDeliveryLineItems so the cart shows the same
                // 12 + 5 breakdown the estimator quoted.
                String(quoteResult.minLoadAdjustment?.requested ?? quoteResult.quantity))}`} className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-stone-300 dark:border-white/15 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 hover:border-[#059F40]/50 dark:hover:border-[#059F40]/50 text-stone-800 dark:text-white text-sm font-semibold transition-all" data-id="element-1075" data-mp="2l1fzo10hitvb">
                          <FileText className="w-4 h-4 text-[#059F40]" data-id="element-1076" data-mp="s2hgvllk6zx1" />
                          View Material
                        </Link>}
                    </div>
                  </div>

                  {/* Delivery Zone Note */}
                  {addressUnverified ? <div className="mt-4 rounded-lg border border-amber-400/50 bg-amber-400/10 p-3.5" data-id="element-1077" data-mp="id7mfz38t55r">
                      <div className="flex items-start gap-2.5" data-id="element-1078" data-mp="4df00fyr1b3l">
                        <Info className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-1079" data-mp="9c9ifgst0xn6" />
                        <div className="flex-1 min-w-0 text-left" data-id="element-1080" data-mp="3lvca8ho2g356">
                          <p className="text-sm font-bold text-amber-200 leading-snug" data-id="element-1081" data-mp="3lxooajqpdtcy">
                            We'll confirm your address before dispatch
                          </p>
                          <p className="mt-1 text-xs text-white/80 leading-relaxed" data-id="element-1082" data-mp="3vv33yh4sye47">
                            This estimate covers the materials only. Once you
                            place the order, dispatch will call you to verify
                            the delivery address and quote the final delivery
                            fee — usually within one business hour.
                          </p>
                          <a href="tel:206-619-3742" className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold text-[#22c95c] hover:text-white transition-colors" data-id="element-1083" data-mp="igpx7pouh4oz">
                            <Phone className="w-3 h-3" data-id="element-1084" data-mp="rftin0bhhw6n" />
                            Prefer to call now? (206) 619-3742
                          </a>
                        </div>
                      </div>
                    </div> : quoteResult.deliveryZone !== null ? <p className="mt-4 text-xs text-stone-700 dark:text-white/70 leading-relaxed text-center font-medium" data-id="element-1085" data-mp="2lv8pix6h3pr9">
                      Delivery fee based on your{' '}
                      <Link to={`/delivery-zones?${estimatorReturnParams}`} className="text-[#059F40] hover:underline" data-id="element-1086" data-mp="2qouv65e7ytdk">
                        delivery zone
                      </Link>
                      .
                    </p> : <div className="mt-3 flex items-center justify-center text-xs text-[#059F40]" data-id="element-1087" data-mp="22wnz77ceooeo">
                      <Info className="w-3.5 h-3.5 mr-1.5 shrink-0" data-id="element-1088" data-mp="27w8zpm7854ti" />
                      Your address is outside standard zones. Delivery fee may
                      apply —{' '}
                      <Link to={`/delivery-zones?${estimatorReturnParams}`} className="font-semibold ml-1 hover:underline" data-id="element-1089" data-mp="3jb64q5jt04mi">
                        check delivery zones
                      </Link>
                    </div>}
                </motion.div>}

              {/* Restart — only appears once the customer has gone through the
               flow far enough that a price is showing (material selected and
               priced). Before that there's nothing to start over. Icon-only:
               the "Start over" label is dropped, the RotateCcw glyph carries
               the meaning (and the aria-label keeps it accessible). Resets to
               a fresh estimator and re-anchors the live ticker. */}
              {showMaterialRow && material && productMap[material] && <div className="mt-4 flex justify-end" data-id="element-1090" data-mp="nyie1lj2w5nq">
                  <button type="button" onClick={() => {
              handleCloseEstimator();
              setShowEstimator(true);
              setTimeout(slideToTicker, 200);
            }} className={`inline-flex items-center justify-center transition-colors p-2 -mr-2 rounded ${isDark ? 'text-white/40 hover:text-white/70' : 'text-stone-500 hover:text-stone-800'}`} aria-label="Start over" title="Start over" data-id="element-1091" data-mp="jxq74yxoicum">
                    <RotateCcw className="w-4 h-4" data-id="element-1092" data-mp="2sgq9rw5hd2vf" />
                  </button>
                </div>}
            </motion.div>}
        </AnimatePresence>

        {/* Trust Badges */}
      </div>
      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-1093" data-mp="2jhw99up13scl" />
    </section>;
}
export default Hero;